/**
 * @file HaulageJobsPlanning.js
 * @version 1.27.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-29
 * @modified 2026-11-03
 * @description Haulage / Bag Jobs planning board — a copy of the Transport
 *              Planning board fed by bagged/tote customer-order lines plus
 *              haulage jobs (HO-). Haulage rows default to EA69 BAG / Excel Agri
 *              (read-only) / Dan Parnell and persist to orders.haulage_jobs.
 *              Stock-supplied lines (H J Ward Agriculture) never raise a PO, so
 *              they arrive with is_stock_line and are keyed by order line (SL-)
 *              rather than PO id — see normaliseBoardRow below.
 *              Each row has a three-dots menu beside the right-click one;
 *              both offer "Archive order…" / "Archive PO…", one row at a time,
 *              through the search screens' own flows (hooks/useBoardArchive).
 *              Original Transport Planning header follows for reference:
 *              Transport planning board: per-line tonnage + vehicle load checker
 *              (payload + compartment fit), driver/haulier/vehicle assignment,
 *              drag-drop sequencing, day summary rollup, bulk row selection
 *              (group / move / bulk-set), multi-row block drag with auto-scroll.
 *
 *              1.17.1 (2026-10-07): the load window's layouts are greyed with
 *              the reason on a locked or past day (the server now refuses a
 *              reorder or door layout there), rather than applied and undone.
 *
 *              1.18.0 (2026-10-12): the arrow keys move between the time
 *              fields (utils/timeFieldNav), as on the bulk board.
 *
 *              1.19.0 (2026-10-12, Cam): SELF-BILLED purchase orders with a
 *              pack line are rows here (utils/selfBilledPo — keyed
 *              sb:<PO>:<line>, badged "Self-billed"), as on the bulk board:
 *              planned like any row, never required to be; never in the mill
 *              emails; driver paperwork leaves out the unplanned ones and the
 *              driver and office send windows say which.
 *              1.19.1 (2026-10-13, review): one someone else carries is out
 *              of driver paperwork, the loads, runs and the day's counts.
 *              1.20.0 (2026-10-14, Cam): an order on one of its option dates
 *              that is not its delivery date (utils/planningDay
 *              isOptionCandidate) counts only as a candidate — no load,
 *              compartment, drop number or run, not in the day / office
 *              summary, paperwork or mill emails. Del. Inst. is the order's
 *              instructions with the PO's own text (worked out by the server);
 *              the office summary no longer prints them twice.
 *
 *              1.21.0 (2026-10-14, Cam): driver paperwork straight to the
 *              drivers — the send window says when a pack would go to the
 *              haulier's address because the driver has none (forwarded
 *              from admin@ it bounced at iCloud), lets the driver's own
 *              address be added there, and can Cc the office
 *              (utils/driverPaperworkRecipients, shared by both boards).
 *
 *              1.22.0 (2026-10-15, Cam): a refused order's redelivery
 *              (utils/redelivery) is a row on its redelivery date, badged
 *              "Redelivery" and keyed by its order line for time / order /
 *              doors / runs (utils/planningRows boardKeyOf); "Refused —
 *              redeliver…" / "Edit redelivery…" in each row's menus
 *              (hooks/useBoardRedeliver). Not moved to another date or board
 *              from here, its quantity not changed (the whole line goes).
 *
 *              1.23.0 (2026-10-15, Cam): the collection time is PlanTimeInput
 *              (faded and typed over; 10.5 → 10:30, 1012 → 10:12); one
 *              planner at a time on the bag board (hooks/usePlanningControl,
 *              PlanningControlBar) — someone else planning it makes this tab
 *              view only, with the hand-over prompts.
 *              1.23.2 (2026-10-23): that control is per board DAY now
 *              (hooks/usePlanningControl 1.1.0) — this board claims the day it
 *              shows; another planner on another day plans it freely.
 *              1.25.0 (2026-10-26, Cam): an outside haulier saved on an
 *              order's row (the haulier cell, a driver bringing their
 *              haulier, the bulk edit) asks once per change whether to raise
 *              the order's PO-H and send it (hooks/useOutsideHaulierPoh,
 *              OutsideHaulierPohDialog). Not for haulage jobs (always ours),
 *              self-billed or redelivery rows; never on a fixed day.
 *              1.26.0 (2026-10-30, Cam): the header's emailed notices are
 *              always open, with a red "email not delivered" notice beside
 *              them when any of the day's transport emails bounced or failed
 *              — the bulk board's own component (TransportEmailNotices), so
 *              the two boards no longer keep a copy each; it opens Plan
 *              History on its Emails tab.
 *              1.27.0 (2026-11-03, Cam — the office summary's order): the
 *              summary is sent grouped by haulier · driver · vehicle as the
 *              bulk board groups (utils/boardGroups): not yet assigned first,
 *              then our lorries by first collection, then outside hauliers';
 *              each lorry's rows by collection time (a run at its first
 *              time), then board order, with each row's D#; `groups` and the
 *              flat `deliveries` in the same order.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { normalizeTimeValue } from '../utils/planningTime';
import PlanTimeInput from './PlanTimeInput';
import PlanningControlBar from './PlanningControlBar';
import usePlanningControl from '../hooks/usePlanningControl';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import PaperworkSendModal from './PaperworkSendModal';
import {
  buildDriverPaperworkRecipients, fallbackNote, fallbackWarning, withDriverEmail, saveDriverEmail
} from '../utils/driverPaperworkRecipients';
import PaperworkHistoryModal from './PaperworkHistoryModal';
import PlanHistoryModal from './PlanHistoryModal';
import TransportEmailNotices, { HeaderNotice } from './TransportEmailNotices';
import ChangeQuantityModal from './ChangeQuantityModal';
import { revisedPosFrom } from '../utils/poRevisions';
import { analyzeVehicleLoad, segmentLoads, fitSeverity, efficiencySeverity, SEVERITY_COLOR, compartmentsByRow, dropNumbersByRow, loadTimeLabel } from '../utils/vehicleLoad';
import { officeSummaryLayout } from '../utils/boardGroups';
import ProgressRing from './ProgressRing';
import VehicleLoadModal from './VehicleLoadModal';
import CustomerOrderModal from './CustomerOrderModal';
import './SearchLayout.css';
import './HaulageJobsPlanning.css';
import './AuditHistoryModal.css';
import LoadingSpinner from './LoadingSpinner';
import SearchableDropdown from './SearchableDropdown';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import SendToSupplierFlow from './SendToSupplierFlow';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import OfficeSummaryModal from './OfficeSummaryModal';
import { useDragSelect } from '../hooks/useDragSelect';
import { hasPermission, badgeOf } from '../utils/permissions';
import usePersistedState from '../hooks/usePersistedState';
import useTableContextMenu from '../hooks/useTableContextMenu';
import TableContextMenu from './TableContextMenu';
import RowActionsMenu from './RowActionsMenu';
import useBoardArchive from '../hooks/useBoardArchive';
import useBoardRedeliver from '../hooks/useBoardRedeliver';
import useOutsideHaulierPoh from '../hooks/useOutsideHaulierPoh';
import {
  REDELIVERY_LABEL, REDELIVERY_DATE_REASON, REDELIVERY_BOARD_REASON, REDELIVERY_QTY_REASON,
  redeliveryTitle, redeliveryRowPlanned, storageLineIdOf
} from '../utils/redelivery';
import PlanningBulkEditModal from './PlanningBulkEditModal';
import {
  HAULAGE_KEY_PREFIX, isHaulageKey, isStockKey,
  haulageJobIdOf, stockLineIdOf, boardKeyOf, rowReference, transportChangesUrl, RowReferenceLink,
  UNKNOWN_VEHICLE, isExcelHaulier, newRunKey, saveRun, withRunKey
} from '../utils/planningRows';
import EntityLink from './EntityLink';
import { isOptionCandidate } from '../utils/planningDay';
import { formatQuantity, formatTonnes } from '../utils/numberFormatters';
import {
  plannableRowIds, driverPaperworkRows, millEmailRows, ourDeliveryRows, selfBilledRowPlanned, unplannedSelfBilledWarning,
  SELF_BILLED_LABEL, SELF_BILLED_BADGE_TITLE, SELF_BILLED_BOARD_REASON, SELF_BILLED_DATE_REASON, SELF_BILLED_NO_ORDER_REASON
} from '../utils/selfBilledPo';
import { thirdPartyBadgeTitle } from '../utils/thirdPartyBoard';

// First three alphabetic characters of a string, uppercased.
const letters3 = (s) => (s || '').replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase();

// Sentinel "vehicle" for a driver whose real vehicle isn't in the fleet (external
// hauliers). Its payload is set per driver per day (transport.unknown_vehicle_payloads)
// rather than living on a vehicles-table row.

// Fixed defaults for haulage-job rows on the Haulage/Bag board. The haulier is
// read-only (always Excel Agri); the user may change vehicle + driver.
const HAULAGE_DEFAULT_HAULIER = 'Excel Agri';
const HAULAGE_DEFAULT_DRIVER = 'Dan Parnell';
const HAULAGE_DEFAULT_VEHICLE = 'EA69 BAG';

// Compact supplier/mill label for the board column. Known suppliers get a fixed
// abbreviation; "MVFS - X" bodies use the first three letters of X; everything
// else uses the first three letters of the name. Full name + account number are
// shown in the cell's tooltip.
const abbreviateSupplier = (name) => {
  if (!name) return '';
  const n = String(name).trim();
  const map = {
    'nwf agriculture': 'NWF',
    'forfarmers - exeter': 'FF',
    'mole valley feed solutions': 'MVFS',
    'mvfs - lifton': 'LIF',
    'mvfs - risdon': 'RIS',
    // Us (own stock / group companies) → EA.
    'excel agri': 'EA',
    'stock': 'EA',
    'h j ward agriculture': 'EA'
  };
  const fixed = map[n.toLowerCase()];
  if (fixed) return fixed;
  const mvfs = n.match(/^mvfs\s*-\s*(.+)$/i);
  if (mvfs) return letters3(mvfs[1]);
  return letters3(n);
};

function HaulageJobsPlanning({ user, onNavigate }) {
  // Holds the permission; whether this tab may plan now is canEdit, below.
  const canPlan = hasPermission(user, 'transport_planning');
  // The page is keyed by selectedDate so persisting it is the whole game —
  // returning users land back on the date they were planning.
  const [selectedDate, setSelectedDate] = usePersistedState(
    'transportPlanning.selectedDate',
    (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    })()
  );
  // One planner at a time on this board (hooks/usePlanningControl).
  const control = usePlanningControl('bag', { canPlan, date: selectedDate });
  const controlBlock = control.blockReason;
  const canEdit = canPlan && !controlBlock;
  const noEditReason = !canPlan ? 'You do not have permission to edit transport planning' : controlBlock;
  const [orders, setOrders] = useState([]);
  // Every row on the board — named apart for the summaries, which narrow it
  // to our own deliveries (no Customer Collection) under the name `orders`.
  const boardOrders = orders;
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [hauliers, setHauliers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleSpecs, setVehicleSpecs] = useState({}); // registration -> full spec, for the load checker
  const [unknownPayloads, setUnknownPayloads] = useState({}); // driverName -> payload, for the "Unknown" vehicle
  const [changesModal, setChangesModal] = useState({ open: false, order: null, changes: [], loading: false });
  const [expandedChanges, setExpandedChanges] = useState(new Set());
  const [isLocked, setIsLocked] = useState(false);
  const [isPastDate, setIsPastDate] = useState(false); // True if date is before today (auto-locked)
  const [isSnapshot, setIsSnapshot] = useState(false);
  const [lockLoading, setLockLoading] = useState(false); // Loading state for lock operations
  const [unlockModal, setUnlockModal] = useState({ open: false, password: '', error: null });
  // A past day unlocked for correction: until when (it re-locks itself).
  const [unlockedUntil, setUnlockedUntil] = useState(null);
  // Order, times, moves, groups, paperwork: fixed while locked, and ALWAYS on
  // a past day — only haulier / driver / vehicle can be corrected after it
  // (services/pastDayUnlockService). Those three cells follow isLocked alone.
  // Someone else planning the board fixes everything, the three cells too.
  const planFixed = isLocked || isPastDate || !!controlBlock;
  const cellsFixed = isLocked || !!controlBlock;
  const [paperworkModal, setPaperworkModal] = useState({ open: false, warnings: [], generating: false, progress: 0 });
  const [haulierSelectionModal, setHaulierSelectionModal] = useState({ open: false, hauliers: [] });
  const [supplierSelectionModal, setSupplierSelectionModal] = useState({ open: false, suppliers: [] });
  const [comingSoonModal, setComingSoonModal] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', type: 'info' });
  const [hauliersWithEmail, setHauliersWithEmail] = useState([]);
  const [paperworkLog, setPaperworkLog] = useState(null);
  const [supplierEmailLog, setSupplierEmailLog] = useState(null);
  const [officeSummaryModal, setOfficeSummaryModal] = useState({ open: false, generating: false });
  const [officeSummaryLog, setOfficeSummaryLog] = useState(null);
  const [expandedAddresses, setExpandedAddresses] = useState(new Set());
  const [datesWithChanges, setDatesWithChanges] = useState(new Set()); // dates (today+future) with unacknowledged changes → warning dot
  const dateSelectorRef = useRef(null);
  const ordersRef = useRef(orders);
  const measureRef = useRef(null);
  const [colWidths, setColWidths] = useState({ haulier: 130, driver: 135, vehicle: 140 });

  // ── Row selection + reorder / context-menu / view state ──
  const [selectedRows, setSelectedRows] = useState(new Set()); // order_line_id set
  // Archive order… / Archive PO… from a row's menus — the search screens' own
  // flows, one row at a time (hooks/useBoardArchive). fetchOrders is declared
  // further down; it is only called once an archive has finished.
  const rowArchive = useBoardArchive({
    user,
    selectedCount: selectedRows.size,
    isSnapshot,
    source: 'Transport planning (bag board)',
    onArchived: () => { setSelectedRows(new Set()); fetchOrders(); },
    showAlert: ({ title, message, type }) => setAlertDialog({ open: true, title, message, type })
  });
  // Refused — redeliver… / Edit redelivery… from a row's menus, one row at a
  // time (hooks/useBoardRedeliver, RedeliverOrderModal).
  const rowRedeliver = useBoardRedeliver({
    user,
    selectedCount: selectedRows.size,
    onDone: () => { setSelectedRows(new Set()); fetchOrders(); },
    showAlert: ({ title, message, type }) => setAlertDialog({ open: true, title, message, type })
  });
  // An outside haulier set on a row whose order has no PO-H: "raise one and
  // send it?" — once per change, never on a fixed day (hooks/useOutsideHaulierPoh).
  const outsidePoh = useOutsideHaulierPoh({
    user,
    date: selectedDate,
    enabled: canEdit && !planFixed && !isSnapshot,
    onChanged: () => fetchOrders(),
    showAlert: ({ title, message, type }) => setAlertDialog({ open: true, title, message, type })
  });
  // Bulk edit of haulier / driver / vehicle / time for the selected rows.
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [moveToModal, setMoveToModal] = useState({ open: false, value: '', targetIds: null });
  const [combineRunModal, setCombineRunModal] = useState({ open: false, value: '', targetIds: null });
  const [moveDateModal, setMoveDateModal] = useState({ open: false, value: '', orderIds: [], count: 0, optionDates: [] });
  const [notesModal, setNotesModal] = useState({ open: false, order: null });
  const [vehicleLoadsCollapsed, setVehicleLoadsCollapsed] = usePersistedState('transportPlanning.vehicleLoadsCollapsed', false);
  const [daySummaryCollapsed, setDaySummaryCollapsed] = usePersistedState('transportPlanning.daySummaryCollapsed', false);
  const [datePickerCollapsed, setDatePickerCollapsed] = usePersistedState('transportPlanning.datePickerCollapsed', false);
  const [driverColorsOn, setDriverColorsOn] = usePersistedState('transportPlanning.driverColorsOn', false);
  const [isolatedDriver, setIsolatedDriver] = useState(null); // null = off; '' = unassigned only; name = that driver + unassigned
  const [showOnlyOptionPlanned, setShowOnlyOptionPlanned] = useState(false); // only rows planned here that also have option dates
  const [optionSectionCollapsed, setOptionSectionCollapsed] = usePersistedState('transportPlanning.optionSectionCollapsed', true);
  const ctxMenu = useTableContextMenu({
    rows: orders,
    selectedIds: selectedRows,
    getRowId: (o) => o.order_line_id
  });
  // "Go to order" — the customer order behind a row. Persist the id (not the
  // object) so the modal reopens on the row after navigating away and back.
  const [orderModalId, setOrderModalId] = usePersistedState('transportPlanning.orderModalId', null);
  const transportScrollRef = useRef(null);          // the vertical scroll container
  const autoScrollRef = useRef({ raf: null, velocity: 0 }); // drag auto-scroll
  const dragBlockRef = useRef(null);                // order_line_ids dragged as a block

  // Re-measure slideout button widths whenever a modal that contains
  // .btn-slideout buttons opens/closes. The .generating flags are listed
  // for the haulier and supplier modals because their footer (with the
  // slideout buttons) is conditionally rendered while generating; toggling
  // remounts the buttons.
  useSlideOutButton([
    unlockModal.open,
    changesModal.open,
    changesModal.loading,
    paperworkModal.open,
    haulierSelectionModal.open,
    haulierSelectionModal.generating,
    supplierSelectionModal.open,
    supplierSelectionModal.generating,
    officeSummaryModal.open,
    officeSummaryModal.generating,
    moveToModal.open,
    moveDateModal.open,
    // The bulk-actions bar (with its slide-out buttons) mounts/unmounts with
    // the selection, so re-measure whenever the selection count changes.
    selectedRows.size,
    comingSoonModal,
    // Counts on the haulier/supplier "Send to N …" buttons change as the
    // user toggles include checkboxes, so the label width changes too.
    haulierSelectionModal.hauliers && haulierSelectionModal.hauliers.filter(h => h.include).length,
    supplierSelectionModal.suppliers && supplierSelectionModal.suppliers.filter(s => s.include).length
  ]);

  // Measure widest haulier/vehicle names to set dynamic column min-widths
  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const measure = (items) => {
      let max = 0;
      items.forEach(text => {
        el.textContent = text;
        max = Math.max(max, el.offsetWidth);
      });
      el.textContent = '';
      return max;
    };
    const pad = 32; // cell padding + dropdown arrow space
    const hw = hauliers.length > 0 ? measure(hauliers) + pad : 180;
    const dw = drivers.length > 0 ? measure(drivers.map(d => d.name)) + pad : 150;
    const vw = vehicles.length > 0 ? measure(vehicles) + pad : 140;
    setColWidths({
      haulier: Math.max(hw, 180),
      driver: Math.max(dw, 150),
      vehicle: Math.max(vw, 140)
    });
  }, [hauliers, drivers, vehicles]);

  // Generate dates: 3 months back through 2 months ahead from today
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(today);
    start.setMonth(start.getMonth() - 3);

    const end = new Date(today);
    end.setMonth(end.getMonth() + 2);

    const current = new Date(start);
    while (current <= end) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      dates.push(`${yyyy}-${mm}-${dd}`);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const dates = generateDates();

  useEffect(() => {
    // Check if selected date is before today (past dates should be auto-locked)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    const isPast = selected < today;
    setIsPastDate(isPast);

    // Fetch lock status from DB, auto-lock if past date
    fetchLockStatus(selectedDate, isPast);
    setSelectedRows(new Set()); // stale row ids on date change
    setIsolatedDriver(null);
    setShowOnlyOptionPlanned(false);
    ctxMenu.reset();
    fetchOrders();
    fetchUnknownPayloads(selectedDate);
    fetchPaperworkLog(selectedDate);
    fetchSupplierEmailLog(selectedDate);
    fetchOfficeSummaryLog(selectedDate);
    fetchDatesWithChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    fetchHauliers();
    fetchDrivers();
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep ordersRef in sync with orders state for use in drag handlers
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  // Scroll date selector to center on selected date on mount
  useEffect(() => {
    if (dateSelectorRef.current) {
      const container = dateSelectorRef.current;
      const selectedIndex = dates.indexOf(selectedDate);
      const buttons = container.querySelectorAll('.date-btn');
      if (selectedIndex >= 0 && buttons[selectedIndex]) {
        const button = buttons[selectedIndex];
        const containerRect = container.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        const scrollLeft = container.scrollLeft + (buttonRect.left + buttonRect.width / 2) - (containerRect.left + containerRect.width / 2);
        container.scrollLeft = scrollLeft;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollDates = (direction) => {
    if (dateSelectorRef.current) {
      const container = dateSelectorRef.current;
      const scrollAmount = 300; // Scroll by approximately 3 date buttons
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const fetchHauliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/hauliers`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setHauliersWithEmail(data.hauliers);
        setHauliers(data.hauliers.map(h => h.name));
      }
    } catch (error) {
      console.error('Failed to load hauliers:', error);
      setHauliers([]);
      setHauliersWithEmail([]);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/drivers`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        // Keep the full driver shape — name, hauliers (for filter), default vehicle (for auto-fill).
        setDrivers(data.drivers);
      }
    } catch (error) {
      console.error('Failed to load drivers:', error);
      setDrivers([]);
    }
  };

  // Load the set of today/future dates that have jobs with unacknowledged
  // changes — drives the warning dots on the date selector.
  const fetchDatesWithChanges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/dates-with-changes`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setDatesWithChanges(new Set(data.dates));
      }
    } catch (error) {
      console.error('Failed to load dates with changes:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/vehicles`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setVehicles(data.vehicles.map(v => v.registration));
        setVehicleSpecs(Object.fromEntries(data.vehicles.map(v => [v.registration, v])));
      }
    } catch (error) {
      console.error('Failed to load vehicles:', error);
      setVehicles([]);
    }
  };


  const fetchPaperworkLog = async (date) => {
    try {
      const response = await fetch(
        `${API_URL}/api/transport/paperwork-log/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setPaperworkLog(data.latest);
      }
    } catch (error) {
      console.error('Failed to load paperwork log:', error);
      setPaperworkLog(null);
    }
  };

  const fetchSupplierEmailLog = async (date) => {
    try {
      const response = await fetch(
        `${API_URL}/api/transport/supplier-email-log/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setSupplierEmailLog(data.latest);
      }
    } catch (error) {
      console.error('Failed to load supplier email log:', error);
      setSupplierEmailLog(null);
    }
  };

  const fetchOfficeSummaryLog = async (date) => {
    try {
      const response = await fetch(
        `${API_URL}/api/transport/office-summary-log/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setOfficeSummaryLog(data.latest);
      }
    } catch (error) {
      console.error('Failed to load office summary log:', error);
      setOfficeSummaryLog(null);
    }
  };

  const fetchLockStatus = async (date, isPast) => {
    setLockLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/transport/lock/${date}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        // The server decides: a past day is locked unless it was unlocked
        // for correction within the last half hour, and re-locks itself.
        setIsLocked(data.isLocked);
        setUnlockedUntil(data.unlockedUntil || null);
      }
    } catch (error) {
      console.error('Failed to load lock status:', error);
      // Default to locked for past dates, unlocked for future
      setIsLocked(isPast);
    } finally {
      setLockLoading(false);
    }
  };

  const saveLockStatus = async (date, locked) => {
    try {
      const response = await fetch(`${API_URL}/api/transport/lock/${date}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isLocked: locked })
      });
      const data = await response.json();
      if (data.success) {
        setIsLocked(data.isLocked);
        setUnlockedUntil(data.unlockedUntil || null);
      }
      return data.success;
    } catch (error) {
      console.error('Failed to save lock status:', error);
      return false;
    }
  };

  /*
   * One request for every selected row: the server writes them in a single
   * transaction and recalculates each order's status once. The board is then
   * re-read rather than patched, so the vehicle loads and summaries match.
   */
  const applyBulkEdit = async (changes) => {
    // Order lines and haulage jobs (HO-) go to different tables.
    const ids = [...selectedRows];
    // Order lines and self-billed POs' rows (sb:<PO>:<line>) alike.
    const orderLineIds = plannableRowIds(ids.filter(id => !isHaulageKey(id)));
    const haulageJobIds = ids.filter(isHaulageKey).map(haulageJobIdOf);
    const editedRows = orders.filter(o => selectedRows.has(o.order_line_id) && !isHaulageKey(o.order_line_id));
    const res = await fetch(`${API_URL}/api/transport/planning/bulk-update`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ date: selectedDate, orderLineIds, haulageJobIds, changes })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) throw new Error(data.error || 'Could not update the rows');
    if ((data.customer_collection_skipped || []).length) {
      const list = data.customer_collection_skipped;
      setAlertDialog({
        open: true,
        title: 'Customer Collection left alone',
        message: `${list.join(', ')} ${list.length === 1 ? 'is' : 'are'} Customer Collection — the customer collects, so no haulier, driver or vehicle was set on ${list.length === 1 ? 'it' : 'them'}. Any time was still applied.`,
        type: 'info'
      });
    }
    setBulkEditOpen(false);
    setSelectedRows(new Set());
    setLastAutoSaved(new Date());
    fetchOrders();
    // An outside haulier: raise the PO-H? (Customer Collection rows were left alone.)
    if ('haulier' in changes) {
      const skipped = new Set(data.customer_collection_skipped || []);
      outsidePoh.afterHaulierChange(editedRows.filter(o => !o.is_customer_collection && !skipped.has(o.order_number)), changes.haulier);
    }
  };
  const bulkEditBlocked = !canEdit ? noEditReason
    : isLocked ? 'This day is locked' : null;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/transport/planning/haulage-orders?date=${selectedDate}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        // Haulage-job rows have no order_line_id — give them a stable synthetic
        // one so the board's row-keyed state/selection logic stays unique, and
        // apply the fixed haulage defaults (EA69 BAG / Excel Agri / Dan Parnell).
        // Stock rows keep their real order_line_id but have no PO, so they get a
        // synthetic PO key for the same reason.
        const normalised = (data.orders || []).map(o => {
          if (o.is_haulage_job) {
            return {
              ...o,
              order_line_id: `${HAULAGE_KEY_PREFIX}${o.haulage_job_id}`,
              // Synthetic PO id keeps the PO-keyed handlers (sequence, scheduled
              // time, door config) unique per HO row instead of colliding on null.
              purchase_order_id: `${HAULAGE_KEY_PREFIX}${o.haulage_job_id}`,
              haulier: HAULAGE_DEFAULT_HAULIER,
              driver: o.driver || HAULAGE_DEFAULT_DRIVER,
              vehicle: o.vehicle || HAULAGE_DEFAULT_VEHICLE
            };
          }
          // A redelivery row likewise, by its order line (utils/planningRows boardKeyOf).
          if (o.is_stock_line || o.is_redelivery) {
            return { ...o, purchase_order_id: boardKeyOf(o) };
          }
          return o;
        });
        setOrders(normalised);
        setIsSnapshot(data.isSnapshot === true);
      } else {
        console.error('Failed to fetch orders:', data.error);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Per-driver "Unknown" vehicle payloads for the selected day.
  const fetchUnknownPayloads = async (date) => {
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/unknown-payloads?date=${date}`, { credentials: 'include' });
      const data = await res.json();
      setUnknownPayloads(data.success && data.payloads ? data.payloads : {});
    } catch (error) {
      console.error('Failed to load unknown vehicle payloads:', error);
      setUnknownPayloads({});
    }
  };

  // Set (or clear) a driver's "Unknown" vehicle payload for the day. Optimistic —
  // the load summaries re-analyse the moment state lands.
  const setUnknownPayload = async (driverName, payload) => {
    const driver = (driverName || '').trim();
    const value = (payload === '' || payload == null) ? null : Number(payload);
    setUnknownPayloads(prev => {
      const next = { ...prev };
      if (value == null) delete next[driver]; else next[driver] = value;
      return next;
    });
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/unknown-payload`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, driverName: driver, payload: value })
      });
      const data = await res.json();
      if (data.success) setLastAutoSaved(new Date());
      else { console.error('Failed to save unknown payload:', data.error); fetchUnknownPayloads(selectedDate); }
    } catch (error) {
      console.error('Error saving unknown payload:', error);
      fetchUnknownPayloads(selectedDate);
    }
  };

  const updateHaulier = async (orderLineId, haulier) => {
    // Haulage jobs are always hauled by Excel Agri — haulier is read-only.
    if (String(orderLineId).startsWith('HO-')) return;
    // Owner-driver: a haulier with a same-named driver (Taylors → Taylors)
    // brings that driver with it, saved in the same request as the haulier.
    const matchDriver = haulier
      ? drivers.find(d => d.name.trim().toLowerCase() === haulier.trim().toLowerCase())
      : null;
    const changes = { haulier: haulier || null, ...(matchDriver ? { driver: matchDriver.name } : {}) };
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/bulk-update`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, orderLineIds: [orderLineId], changes, source: 'Transport planning' })
      });
      const data = await response.json();
      if (data.success) {
        setOrders(prev => prev.map(order =>
          order.order_line_id === orderLineId ? { ...order, ...changes, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
        outsidePoh.afterHaulierChange(orders.filter(o => o.order_line_id === orderLineId), changes.haulier);
      } else {
        console.error('Failed to update haulier:', data.error);
      }
    } catch (error) {
      console.error('Error updating haulier:', error);
    }
  };

  const updateDriver = async (orderLineId, driver) => {
    // Haulage-job rows persist to the haulage endpoint (no owner-driver cascade).
    if (String(orderLineId).startsWith('HO-')) {
      const hj = orders.find(o => o.order_line_id === orderLineId);
      try {
        const r = await fetch(`${API_URL}/api/transport/planning/haulage/update-driver`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ haulageJobId: hj?.haulage_job_id, value: driver })
        });
        const d = await r.json();
        if (d.success) {
          setOrders(prev => prev.map(o => o.order_line_id === orderLineId ? { ...o, driver } : o));
          setLastAutoSaved(new Date());
        }
      } catch (e) { console.error('Error updating haulage driver:', e); }
      return;
    }
    // The driver brings their haulier and vehicle with them — worked out
    // first, then saved in ONE request, so the history reads as one change
    // ("driver, haulier, vehicle") rather than three entries a moment apart.
    // A driver's default haulier is their sole linked one; their default
    // vehicle, else "Unknown" for an outside haulier's driver (payload set
    // later) and nothing for ours. The user can still override afterwards.
    const driverRecord = drivers.find(d => d.name === driver);
    const defaultReg = driverRecord?.default_vehicle_registration;
    const linkedHauliers = driverRecord?.hauliers || [];
    const changes = { driver: driver || null };
    if (driver && linkedHauliers.length === 1) changes.haulier = linkedHauliers[0].name;
    if (driver && defaultReg) {
      changes.vehicle = defaultReg;
    } else if (driver) {
      const row = orders.find(o => o.order_line_id === orderLineId);
      const haulierName = changes.haulier || row?.haulier || '';
      if (haulierName && !isExcelHaulier(haulierName)) changes.vehicle = UNKNOWN_VEHICLE;
    }
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/bulk-update`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, orderLineIds: [orderLineId], changes, source: 'Transport planning' })
      });
      const data = await response.json();
      if (data.success) {
        setOrders(prev => prev.map(order =>
          order.order_line_id === orderLineId ? { ...order, ...changes, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
        // The driver brought their haulier with them.
        if ('haulier' in changes) outsidePoh.afterHaulierChange(orders.filter(o => o.order_line_id === orderLineId), changes.haulier);
      } else {
        console.error('Failed to update driver:', data.error);
      }
    } catch (error) {
      console.error('Error updating driver:', error);
    }
  };

  const updateVehicle = async (orderLineId, vehicle) => {
    if (String(orderLineId).startsWith('HO-')) {
      const hj = orders.find(o => o.order_line_id === orderLineId);
      try {
        const r = await fetch(`${API_URL}/api/transport/planning/haulage/update-vehicle`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ haulageJobId: hj?.haulage_job_id, value: vehicle })
        });
        const d = await r.json();
        if (d.success) {
          setOrders(prev => prev.map(o => o.order_line_id === orderLineId ? { ...o, vehicle } : o));
          setLastAutoSaved(new Date());
        }
      } catch (e) { console.error('Error updating haulage vehicle:', e); }
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/transport/planning/update-vehicle`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderLineId, vehicle })
      });
      const data = await response.json();
      if (data.success) {
        setOrders(prev => prev.map(order =>
          order.order_line_id === orderLineId ? { ...order, vehicle, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
      } else {
        console.error('Failed to update vehicle:', data.error);
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
    }
  };

  // ── Vehicle load analysis (payload + compartment fit per assigned vehicle) ──
  // Groups the day's rows by their assigned vehicle in board order and runs the
  // deterministic checker. Loads split by collection TIME only (a load may
  // collect from several suppliers/mills). Top row = first drop = REAR-most
  // compartment. The result is ordered to mirror the board: grouped by driver,
  // each driver's runs in run order, drivers in table order (top-most first).
  // Board rows and load cards carry driver *names* (orders.order_lines.driver is
  // a varchar, not an FK), so linking one needs a name -> id lookup off the
  // drivers list we already fetch.
  const driverIdByName = useMemo(() => {
    const map = {};
    drivers.forEach(d => { if (d && d.name) map[d.name.toLowerCase()] = d.id; });
    return map;
  }, [drivers]);
  const driverIdFor = (name) => (name ? driverIdByName[String(name).toLowerCase()] : null);

  const vehicleLoads = useMemo(() => {
    // Group by assigned vehicle in board order. The "Unknown" vehicle is shared
    // across drivers, so it sub-groups by driver — each driver gets their own
    // Unknown card + payload.
    const groups = new Map(); // groupKey -> { reg, driver, rows }
    // Our deliveries on the day only (utils/selfBilledPo ourDeliveryRows): the
    // customer collects a Customer Collection order, and an order on one of
    // its option dates that is not its delivery date is planned on another
    // day (utils/planningDay) — neither is on a load of ours here.
    for (const o of ourDeliveryRows(orders)) {
      const reg = (o.vehicle || '').trim();
      if (!reg) continue;
      const driver = (o.driver || '').trim();
      const key = reg === UNKNOWN_VEHICLE ? `${UNKNOWN_VEHICLE}::${driver}` : reg;
      if (!groups.has(key)) groups.set(key, { reg, driver, rows: [] });
      groups.get(key).rows.push(o);
    }
    const out = [];
    for (const [, g] of groups) {
      let spec;
      if (g.reg === UNKNOWN_VEHICLE) {
        // Synthetic spec: just a payload (per driver, per day), no compartments —
        // so the checker reports payload utilisation only, not compartment fit.
        // Payload is left UNDEFINED (not null) when unset: Number(null) is 0,
        // which usablePayload would treat as a real 0T payload, but Number(undefined)
        // is NaN → null, i.e. "no payload set".
        const p = unknownPayloads[g.driver];
        spec = {
          registration: UNKNOWN_VEHICLE,
          payload: (p == null || p === '') ? undefined : Number(p),
          vehicle_type: null, has_moffett: false,
          has_moveable_doors: false, compartment_count: 0
        };
      } else {
        spec = vehicleSpecs[g.reg];
        if (!spec) continue; // unknown/archived vehicle — nothing to check against
      }
      const rows = g.rows;
      // A vehicle does one or more loads/day (collect → deliver → collect …).
      const loads = segmentLoads(rows);
      loads.forEach((ld, idx) => {
        const lineTonnes = ld.rows.map(o => {
          const t = Number(o.line_tonnes);
          return Number.isFinite(t) ? t : (Number(o.quantity) || 0);
        });
        // Product key per drop — enables the same-product compartment-mixing rule.
        const products = ld.rows.map(o => o.product_code || o.product_name || null);
        // User-chosen door layout for this load (stored per PO, shared across the
        // load); null = use the auto-picked tightest layout.
        const overrideDoors = ld.rows.map(o => o.door_config).find(d => Array.isArray(d)) || null;
        const poIds = [...new Set(ld.rows.map(o => o.purchase_order_id).filter(Boolean))];
        // A load can now span several suppliers/mills — surface the distinct set.
        const suppliers = [...new Set(ld.rows
          .map(o => (o.supplier_name || o.contact_name || o.supplier_account_no || '').trim())
          .filter(Boolean))];
        out.push({
          ...analyzeVehicleLoad(spec, lineTonnes, products, overrideDoors),
          rows: ld.rows,
          supplier: suppliers.join(', ') || null,
          time: ld.time,
          times: ld.times,
          timeLabel: loadTimeLabel(ld),
          driver: (ld.rows.find(r => r.driver && r.driver.trim()) || {}).driver || null,
          loadIndex: idx,
          loadCount: loads.length,
          poIds,
          vehicle: spec,
          isUnknown: g.reg === UNKNOWN_VEHICLE,
          unknownDriver: g.reg === UNKNOWN_VEHICLE ? g.driver : null
        });
      });
    }
    // Order the cards to mirror the board rather than by severity: a driver's
    // runs sit together in run order, and the drivers follow the table from the
    // top down. A load's position IS its top-most row, and loads are segmented
    // out of board order, so ordering on that gives run order for free (and
    // still holds if a driver takes more than one vehicle in a day).
    const rowIndex = new Map();
    orders.forEach((o, i) => rowIndex.set(o.order_line_id, i));
    const posOf = (l) => Math.min(...l.rows.map(r => {
      const i = rowIndex.get(r.order_line_id);
      return i == null ? Infinity : i;
    }));
    const loadPos = new Map(out.map(l => [l, posOf(l)]));
    const driverKey = (l) => (l.driver || '').trim();
    // Each driver is pinned to their top-most row on the board.
    const driverPos = new Map();
    for (const l of out) {
      const k = driverKey(l);
      const p = loadPos.get(l);
      if (!driverPos.has(k) || p < driverPos.get(k)) driverPos.set(k, p);
    }
    out.sort((a, b) =>
      (driverPos.get(driverKey(a)) - driverPos.get(driverKey(b)))
      || (loadPos.get(a) - loadPos.get(b)));
    return out;
  }, [orders, vehicleSpecs, unknownPayloads]);

  // Open load (drill-down modal). Keyed by the load's stable identity so the open
  // modal re-resolves to the freshly-analysed load after a config change (rather
  // than holding a stale snapshot) — it refreshes in place instead of closing.
  const [loadModal, setLoadModal] = useState({ open: false, key: null });
  // Driver is in the key so several drivers' "Unknown" loads (all registration
  // 'Unknown') stay distinct.
  const loadKey = (l) => `${l.registration}::${(l.driver || '')}::${l.loadIndex}`;
  // Row → compartments it loads into, numbered from the cab (1) to the rear.
  const compartmentsFor = useMemo(() => compartmentsByRow(vehicleLoads), [vehicleLoads]);
  // Row → its drop number within its load (the "D#" badge by the time).
  const dropNumbers = useMemo(() => dropNumbersByRow(vehicleLoads), [vehicleLoads]);

  const activeLoad = loadModal.open ? vehicleLoads.find(l => loadKey(l) === loadModal.key) : null;

  const updateScheduledTime = async (purchaseOrderId, scheduledTime) => {
    if (isHaulageKey(purchaseOrderId)) {
      const hj = orders.find(o => o.purchase_order_id === purchaseOrderId);
      try {
        const r = await fetch(`${API_URL}/api/transport/planning/haulage/update-scheduled-time`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ haulageJobId: hj?.haulage_job_id, value: scheduledTime })
        });
        const d = await r.json();
        if (d.success) {
          setOrders(prev => prev.map(o => o.purchase_order_id === purchaseOrderId ? { ...o, scheduled_time: scheduledTime } : o));
          setLastAutoSaved(new Date());
        }
      } catch (e) { console.error('Error updating haulage scheduled time:', e); }
      return;
    }
    try {
      // Stock rows have no PO — they're keyed by their order line instead.
      const response = await fetch(`${API_URL}/api/transport/planning/update-scheduled-time`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDate,
          ...(isStockKey(purchaseOrderId)
            ? { orderLineId: stockLineIdOf(purchaseOrderId) }
            : { purchaseOrderId }),
          scheduledTime
        })
      });
      const data = await response.json();
      if (data.success) {
        // Update local state - also clear has_changes since transport planning was just updated
        setOrders(prev => prev.map(order =>
          order.purchase_order_id === purchaseOrderId ? { ...order, scheduled_time: scheduledTime, has_changes: false } : order
        ));
        setLastAutoSaved(new Date());
      } else {
        console.error('Failed to update scheduled time:', data.error);
      }
    } catch (error) {
      console.error('Error updating scheduled time:', error);
    }
  };

  const dismissModified = async (order) => {
    // Call API to update acknowledged_at timestamp for this specific row
    if (order.sequence_id) {
      try {
        await fetch(`${API_URL}/api/audit/transport/${order.sequence_id}/acknowledge`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (error) {
        console.error('Failed to acknowledge changes:', error);
      }
    }
    // Update local state
    setOrders(orders.map(o =>
      o.purchase_order_id === order.purchase_order_id ? { ...o, has_changes: false, acknowledged_at: new Date().toISOString() } : o
    ));
    // Keep the date-selector warning dots in step with the acknowledgment.
    fetchDatesWithChanges();
  };

  const toggleChangeEntry = (idx) => {
    setExpandedChanges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };

  const handleShowChanges = async (order, hasUnacknowledged = true) => {
    setExpandedChanges(new Set());
    setChangesModal({ open: true, order, changes: [], loading: true, hasUnacknowledged });
    try {
      // If viewing history (no unacknowledged changes), fetch all changes; otherwise fetch since last acknowledgment
      const since = hasUnacknowledged ? (order.acknowledged_at || order.updated_at || '') : '';
      // Stock rows have no PO, so their changes are fetched by order line.
      const base = transportChangesUrl(order);
      const url = since ? `${base}?since=${encodeURIComponent(since)}` : base;
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      const changes = Array.isArray(data) ? data : [];
      // Expand all entries by default for unacknowledged changes
      if (hasUnacknowledged && changes.length > 0) {
        setExpandedChanges(new Set(changes.map((_, i) => i)));
      }
      setChangesModal(prev => ({ ...prev, changes, loading: false }));
    } catch (error) {
      console.error('Failed to fetch changes:', error);
      setChangesModal(prev => ({ ...prev, changes: [], loading: false }));
    }
  };

  const formatChangeTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Format change values for display (dates, numbers, etc.)
  const formatChangeValue = (value, fieldName) => {
    if (value === null || value === undefined) return '-';
    // Format dates
    if (fieldName === 'delivery_date' || (fieldName && fieldName.includes('date'))) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        // Date-only strings or midnight timestamps — show date only
        const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
        const isMidnight = date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
        if (isDateOnly || isMidnight) {
          return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
    }
    // Format numbers (quantity, price)
    if (fieldName === 'quantity' || fieldName === 'unit_price') {
      const num = parseFloat(value);
      if (!isNaN(num)) return fieldName === 'quantity' ? formatQuantity(num) : num.toFixed(2);
    }
    return String(value);
  };

  const updateOrderSequence = async (newOrders) => {
    try {
      const planned = newOrders.filter(order => !isOptionCandidate(order));
      // Haulage-job rows persist their sequence to orders.haulage_jobs; ordinary
      // rows to transport_sequences, keyed by PO id — or by order line id for
      // stock rows, which have no PO.
      const poSequence = planned
        .map((order, index) => ({ order, sequence: index + 1 }))
        .filter(({ order }) => !order.is_haulage_job)
        .map(({ order, sequence }) => (order.is_stock_line || order.is_redelivery
          ? { order_line_id: storageLineIdOf(order), sequence }
          : { purchase_order_id: order.purchase_order_id, sequence }));
      const haulageSequence = planned
        .map((order, index) => ({ order, sequence: index + 1 }))
        .filter(({ order }) => order.is_haulage_job);

      const response = await fetch(`${API_URL}/api/transport/planning/update-sequence`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, orderSequence: poSequence })
      });
      const data = await response.json();
      await Promise.all(haulageSequence.map(({ order, sequence }) =>
        fetch(`${API_URL}/api/transport/planning/haulage/update-sequence`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ haulageJobId: order.haulage_job_id, value: sequence })
        }).catch(e => console.error('Error updating haulage sequence:', e))
      ));
      if (data.success) {
        setLastAutoSaved(new Date());
      } else {
        console.error('Failed to update sequence:', data.error);
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating sequence:', error);
      fetchOrders();
    }
  };

  // Apply an alternative compartment configuration to a load: re-sequence that
  // load's drops to the config's drop order, leaving every other row in place,
  // then persist (same mechanism as drag-drop). dropOrder is line indices,
  // rear-first (= first drop first).
  // Persist a load's chosen door layout (override) for every PO in the load, and
  // mirror it into local state so the load re-analyses immediately. doors = null
  // clears the override (reverts to the auto-picked layout).
  const persistLoadDoorConfig = async (load, doors) => {
    const poIds = load?.poIds && load.poIds.length
      ? load.poIds
      : [...new Set((load?.rows || []).map(r => r.purchase_order_id).filter(Boolean))];
    if (!poIds.length) return;
    setOrders(prev => prev.map(o =>
      poIds.includes(o.purchase_order_id) ? { ...o, door_config: doors } : o));
    // Split the synthetic keys (haulage jobs 'HO-', stock lines 'SL-') from real
    // PO ids — each persists through its own endpoint / key.
    const realPoIds = poIds.filter(id => !isHaulageKey(id) && !isStockKey(id));
    const stockLineIds = poIds.filter(isStockKey).map(stockLineIdOf);
    const haulageJobIds = poIds.filter(isHaulageKey)
      .map(haulageJobIdOf);
    try {
      if (realPoIds.length || stockLineIds.length) {
        const res = await fetch(`${API_URL}/api/transport/planning/update-door-config`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: selectedDate, purchaseOrderIds: realPoIds, orderLineIds: stockLineIds, doorConfig: doors })
        });
        const data = await res.json();
        if (data.success) setLastAutoSaved(new Date());
        else { console.error('Failed to save door config:', data.error); fetchOrders(); }
      }
      await Promise.all(haulageJobIds.map(id =>
        fetch(`${API_URL}/api/transport/planning/haulage/update-door-config`, {
          method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ haulageJobId: id, value: doors })
        }).catch(e => console.error('Error saving haulage door config:', e))
      ));
      if (haulageJobIds.length && !realPoIds.length && !stockLineIds.length) setLastAutoSaved(new Date());
    } catch (error) {
      console.error('Error saving door config:', error);
      fetchOrders();
    }
  };

  // Apply a configuration chosen in the load modal: optionally re-sequence the
  // load's drops (dropOrder) and/or set its door layout (doors). Either may be
  // omitted — doors null leaves the door override untouched.
  const applyLoadConfig = (load, dropOrder, doors) => {
    if (!load?.rows) return;
    if (Array.isArray(dropOrder) && !dropOrder.every((v, i) => v === i)) {
      const reordered = dropOrder.map(i => load.rows[i]).filter(Boolean);
      if (reordered.length === load.rows.length) {
        const positions = load.rows
          .map(r => orders.findIndex(o => o.order_line_id === r.order_line_id))
          .filter(p => p >= 0)
          .sort((a, b) => a - b);
        if (positions.length === reordered.length) {
          const newOrders = [...orders];
          positions.forEach((pos, k) => { newOrders[pos] = reordered[k]; });
          setOrders(newOrders);
          updateOrderSequence(newOrders);
        }
      }
    }
    if (doors !== undefined) persistLoadDoorConfig(load, doors);
  };

  // Consolidated Customer/Order notes for a row (account + sales-order level).
  const openNotes = (order) => setNotesModal({ open: true, order });
  const orderHasNotes = (order) =>
    !!((order.customer_notes && order.customer_notes.trim()) || (order.order_notes && order.order_notes.trim()));

  // ── Row selection helpers ──────────────────────────────────────────────
  const toggleRowSelected = (order) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(order.order_line_id)) next.delete(order.order_line_id);
      else next.add(order.order_line_id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedRows(prev =>
      prev.size === orders.length && orders.length > 0
        ? new Set()
        : new Set(orders.map(o => o.order_line_id))
    );
  };

  // Click-drag across the checkboxes to paint a selection (same UX as Search
  // Invoices). setChecked is driven per-row by the shared useDragSelect hook.
  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedRows(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
      });
    }
  });

  // ── Reorder actions (operate on an explicit set of order_line_ids) ──────
  // Reposition the given rows as a contiguous block, preserving their relative
  // order, inserting at `insertAt` within the non-block rows.
  const reorderIds = (idSet, insertAt) => {
    if (planFixed || !idSet || idSet.size === 0) return;
    const blockRows = orders.filter(o => idSet.has(o.order_line_id));
    if (!blockRows.length) return;
    const rest = orders.filter(o => !idSet.has(o.order_line_id));
    const clamped = Math.max(0, Math.min(insertAt, rest.length));
    const newOrders = [...rest.slice(0, clamped), ...blockRows, ...rest.slice(clamped)];
    setOrders(newOrders);
    updateOrderSequence(newOrders);
  };

  // Send the given rows to the top (grouped, order preserved).
  const sendIdsToTop = (idSet) => reorderIds(idSet, 0);

  // Group the given rows together under the top-most of them.
  const groupIds = (idSet) => {
    if (!idSet || idSet.size < 2) return;
    const firstIdx = orders.findIndex(o => idSet.has(o.order_line_id));
    if (firstIdx < 0) return;
    const before = orders.slice(0, firstIdx).filter(o => !idSet.has(o.order_line_id)).length;
    reorderIds(idSet, before);
  };

  // Combine run: make the selected rows ONE run for one vehicle and group them
  // together. A run is one load whatever its rows' times (segmentLoads), so a
  // driver can collect at 06:00 at one mill and 07:00 at the next en route, then
  // deliver the lot. Each row keeps its own time unless one is entered here, in
  // which case they all take it. The vehicle summaries are a useMemo over
  // `orders`, so they re-analyse as soon as state lands.
  const applyCombineRun = async () => {
    if (planFixed) { setCombineRunModal({ open: false, value: '', targetIds: null }); return; }
    const idSet = combineRunModal.targetIds instanceof Set
      ? combineRunModal.targetIds
      : new Set(combineRunModal.targetIds || []);
    if (idSet.size === 0) { setCombineRunModal({ open: false, value: '', targetIds: null }); return; }

    const typed = String(combineRunModal.value || '').trim();
    const normalized = typed ? normalizeTimeValue(typed) : null;
    if (typed && !/^\d{2}:\d{2}$/.test(normalized)) {
      setAlertDialog({
        open: true,
        title: 'Invalid Time',
        message: 'Enter a collection time — e.g. 9, 14:30 or 2pm — or leave it blank to keep each row\'s own time.',
        type: 'warning'
      });
      return;
    }

    // A Customer Collection order is in no vehicle's run (the server refuses it
    // too), nor is an option-date candidate (planned on another day).
    const blockRows = ourDeliveryRows(orders).filter(o => idSet.has(o.order_line_id));
    if (!blockRows.length) { setCombineRunModal({ open: false, value: '', targetIds: null }); return; }

    // A run belongs to one vehicle — warn rather than silently not combining.
    const regs = [...new Set(blockRows.map(o => (o.vehicle || '').trim()).filter(Boolean))];
    if (regs.length > 1) {
      setAlertDialog({
        open: true,
        title: 'Different Vehicles',
        message: `These rows are on ${regs.length} different vehicles (${regs.join(', ')}). A run belongs to one vehicle — assign them to the same vehicle first.`,
        type: 'warning'
      });
      return;
    }

    // Group + stamp the run (and any shared time) in a single state update so
    // the summaries don't flash through an intermediate arrangement.
    const runKey = newRunKey();
    const rest = orders.filter(o => !idSet.has(o.order_line_id));
    const firstIdx = orders.findIndex(o => idSet.has(o.order_line_id));
    const insertAt = orders.slice(0, firstIdx).filter(o => !idSet.has(o.order_line_id)).length;
    const stamped = blockRows.map(o => ({ ...o, ...(normalized ? { scheduled_time: normalized } : {}), has_changes: false }));
    const newOrders = withRunKey([...rest.slice(0, insertAt), ...stamped, ...rest.slice(insertAt)], blockRows, runKey);
    setOrders(newOrders);
    setCombineRunModal({ open: false, value: '', targetIds: null });

    // Persist the new order, the run, then (if one was entered) the shared time.
    updateOrderSequence(newOrders);
    const poIds = [...new Set(blockRows.map(r => r.purchase_order_id).filter(Boolean))];
    try {
      await saveRun(selectedDate, blockRows, runKey);
      if (normalized) {
        await Promise.all(poIds.map(poId =>
          isHaulageKey(poId)
          ? fetch(`${API_URL}/api/transport/planning/haulage/update-scheduled-time`, {
              method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ haulageJobId: haulageJobIdOf(poId), value: normalized })
            })
          : fetch(`${API_URL}/api/transport/planning/update-scheduled-time`, {
              method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                date: selectedDate,
                // Stock rows are keyed by order line — they have no PO.
                ...(isStockKey(poId) ? { orderLineId: stockLineIdOf(poId) } : { purchaseOrderId: poId }),
                scheduledTime: normalized
              })
            })
        ));
      }
      setLastAutoSaved(new Date());
    } catch (error) {
      console.error('Failed to combine run:', error);
      setAlertDialog({ open: true, title: 'Could not combine', message: error.message, type: 'error' });
      fetchOrders();
    }
  };

  // Split from run: the rows go back to being grouped by their times.
  const splitFromRun = async (rows) => {
    const inRun = (rows || []).filter(o => o.run_key);
    if (planFixed || !inRun.length) return;
    setOrders(prev => withRunKey(prev, inRun, null));
    try {
      await saveRun(selectedDate, inRun, null);
      setLastAutoSaved(new Date());
    } catch (error) {
      console.error('Failed to split run:', error);
      setAlertDialog({ open: true, title: 'Could not split', message: error.message, type: 'error' });
      fetchOrders();
    }
  };

  // Move to…: group the target rows and drop them directly beneath the row
  // currently showing the (1-based) number entered in the modal.
  const applyMoveTo = () => {
    const idSet = moveToModal.targetIds instanceof Set
      ? moveToModal.targetIds
      : new Set(moveToModal.targetIds || []);
    // Row numbers count planned rows only (option-date candidates are numberless).
    const plannedList = orders.filter(o => !isOptionCandidate(o));
    const n = parseInt(moveToModal.value, 10);
    if (!Number.isFinite(n) || n < 1 || n > plannedList.length) {
      setAlertDialog({
        open: true,
        title: 'Invalid Row Number',
        message: `Please enter a row number between 1 and ${plannedList.length}.`,
        type: 'warning'
      });
      return;
    }
    if (idSet.size === 0) { setMoveToModal({ open: false, value: '', targetIds: null }); return; }
    // Insert the block directly beneath the n-th planned row.
    const targetRow = plannedList[n - 1];
    const targetIdx = orders.findIndex(o => o.order_line_id === targetRow.order_line_id);
    const insertAt = orders.slice(0, targetIdx + 1).filter(o => !idSet.has(o.order_line_id)).length;
    reorderIds(idSet, insertAt);
    setMoveToModal({ open: false, value: '', targetIds: null });
  };

  // Auto-group: reorder the whole table so rows sit together by driver, keeping
  // each driver's rows in their existing order (group order = first appearance;
  // unassigned rows form their own group).
  const autoGroupByDriver = () => {
    if (planFixed) return;
    const groups = [];
    const byKey = new Map();
    for (const o of orders) {
      // NUL-prefixed so the unassigned bucket can't collide with a driver
      // actually named "unassigned". Keep the \u0000 escape: a literal NUL
      // byte here makes plain grep treat the whole file as binary and
      // silently report no matches anywhere in it.
      const key = (o.driver || '').trim() || '\u0000unassigned';
      if (!byKey.has(key)) { const g = []; byKey.set(key, g); groups.push(g); }
      byKey.get(key).push(o);
    }
    const newOrders = groups.flat();
    setOrders(newOrders);
    updateOrderSequence(newOrders);
  };

  // Move the given rows' customer orders (and their linked POs) to another
  // delivery date. delivery_date lives on the order, so this moves the whole
  // order(s) the rows belong to; the rows then leave the current day's board.
  const openMoveDate = (rows) => {
    const orderIds = [...new Set(rows.map(o => o.id).filter(Boolean))];
    // Collect the target orders' option dates (deduped, in preference order) to
    // offer them as quick picks below the date picker.
    const seen = new Set();
    const optionDates = [];
    rows.forEach(o => (Array.isArray(o.option_dates) ? o.option_dates : []).forEach(od => {
      if (od && od.date && !seen.has(od.date)) { seen.add(od.date); optionDates.push(od); }
    }));
    optionDates.sort((a, b) => (a.preference - b.preference) || String(a.date).localeCompare(String(b.date)));
    setMoveDateModal({ open: true, value: '', orderIds, count: rows.length, optionDates });
  };
  /*
   * Moving a delivery date moves the dates on the order's POs — and a PO the
   * supplier (or haulier) already holds now says the wrong day. The server
   * names those (`resend`); the user is asked to send the revised version, and
   * each email says it is a new delivery date (SendPOEmailModal textFor).
   */
  // Paperwork send/history (PaperworkSendModal / PaperworkHistoryModal).
  const [officeEmail, setOfficeEmail] = useState('admin@excel-agri.com');
  const [historyModal, setHistoryModal] = useState({ open: false, kind: null });
  // Plan History: the whole day — changes, moves, board actions, emails.
  const [planHistoryOpen, setPlanHistoryOpen] = useState(false);
  // Which tab Plan History opens on — Emails, unless asked otherwise.
  const [planHistoryTab, setPlanHistoryTab] = useState('email');
  const openPlanHistory = (tab = 'email') => { setPlanHistoryTab(tab); setPlanHistoryOpen(true); };
  const [resendOffer, setResendOffer] = useState(null); // { pos, message }
  const [resendFlow, setResendFlow] = useState(null);   // pos being sent

  /*
   * Change quantity: the same change as editing the order (ChangeQuantityModal).
   * A PO the supplier or haulier already holds comes back as a revision to
   * send, through the same prompt as a moved date.
   */
  const [changeQtyRow, setChangeQtyRow] = useState(null);
  const onQuantityChanged = (result) => {
    const row = changeQtyRow;
    setChangeQtyRow(null);
    setLastAutoSaved(new Date());
    fetchOrders();
    fetchDatesWithChanges();
    const done = `${row?.order_number || 'Order'}: quantity changed.`;
    const pos = revisedPosFrom(result);
    if (pos.length) {
      setResendOffer({
        pos,
        message: `${done} ${pos.map(p => `${p.purchase_order_number} (${p.supplier_name || (p.po_type === 'haulage' ? 'haulier' : 'supplier')})`).join(', ')} `
          + `had already been sent with the old quantity. Send the revised ${pos.length === 1 ? 'version' : 'versions'}?`
      });
    } else {
      setAlertDialog({ open: true, title: 'Quantity Changed', message: done, type: 'success' });
    }
  };
  const prettyDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB') : '');
  const offerResend = (data, doneMessage) => {
    const list = Array.isArray(data?.resend) ? data.resend : [];
    if (!list.length) return false;
    const pos = list.map(p => ({
      ...p,
      revision: {
        summary: `new delivery date ${prettyDate(p.new_date)}`,
        changes: [`delivery date ${prettyDate(p.old_date) || 'none'} → ${prettyDate(p.new_date)}`]
      }
    }));
    setResendOffer({
      pos,
      message: `${doneMessage ? `${doneMessage} ` : ''}`
        + `${pos.map(p => `${p.purchase_order_number} (${p.supplier_name || (p.po_type === 'haulage' ? 'haulier' : 'supplier')})`).join(', ')} `
        + `had already been sent with the old date. Send the revised ${pos.length === 1 ? 'version' : 'versions'} with the new date?`
    });
    return true;
  };

  const applyMoveDate = async () => {
    const { orderIds, value } = moveDateModal;
    if (!value || !orderIds.length) return;
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/move-delivery-date`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerOrderIds: orderIds, newDate: value })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to move delivery date');
      setMoveDateModal({ open: false, value: '', orderIds: [], count: 0, optionDates: [] });
      setSelectedRows(new Set());
      setLastAutoSaved(new Date());
      fetchOrders();
      fetchDatesWithChanges();
      const dLabel = new Date(value + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      // The old day's haulier / driver / vehicle are cleared by the move (server).
      const cleared = data.transport_cleared?.message || '';
      const moved = `${data.movedOrders} order${data.movedOrders === 1 ? '' : 's'} moved to ${dLabel}.${cleared ? ` ${cleared}` : ''}`;
      if (!offerResend(data, moved)) {
        setAlertDialog({ open: true, title: 'Delivery Date Moved', message: moved, type: cleared ? 'warning' : 'success' });
      }
    } catch (e) {
      console.error('Failed to move delivery date:', e);
      setAlertDialog({ open: true, title: 'Error', message: `Failed to move delivery date: ${e.message}`, type: 'error' });
    }
  };

  // ── Option dates ────────────────────────────────────────────────────────
  const fmtOptDay = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
    return new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };
  const optionTooltip = (order) => {
    const list = Array.isArray(order.option_dates) ? order.option_dates : [];
    const parts = list.map(o => `${fmtOptDay(o.date)} (P${o.preference})`);
    const planned = order.delivery_date ? ` · Planned: ${fmtOptDay(order.delivery_date)}` : '';
    return `Option dates: ${parts.join(', ') || '—'}${planned}${order.is_selected_day ? '' : ' — not planned on this day'}`;
  };

  // "Plan on this day": commit the given orders to the currently-viewed day
  // (moves delivery_date to it, auto-deselecting the other option days).
  const planOnThisDay = async (rows) => {
    const orderIds = [...new Set(rows.map(o => o.id).filter(Boolean))];
    if (!orderIds.length || planFixed) return;
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/move-delivery-date`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerOrderIds: orderIds, newDate: selectedDate })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to plan on this day');
      setSelectedRows(new Set());
      setLastAutoSaved(new Date());
      fetchOrders();
      fetchDatesWithChanges();
      // Choosing another of its option days is a date move: the old day's
      // haulier / driver / vehicle are cleared (server) — said here.
      const cleared = data.transport_cleared?.message || '';
      if (!offerResend(data, `Planned on this day.${cleared ? ` ${cleared}` : ''}`) && cleared) {
        setAlertDialog({ open: true, title: 'Planned on this day', message: cleared, type: 'warning' });
      }
    } catch (e) {
      console.error('Failed to plan on this day:', e);
      setAlertDialog({ open: true, title: 'Error', message: `Failed to plan on this day: ${e.message}`, type: 'error' });
    }
  };

  // "Go to order" (or job) — in the right-click menu and the three-dots menu
  // alike. Opening it only shows it, so it stays usable for view-only accounts.
  const goToOrderAction = (row) => ({
    key: 'go-to-order',
    readSafe: true,
    icon: row.is_haulage_job ? 'local_shipping' : 'shopping_cart',
    label: row.is_haulage_job
      ? `Go to job ${row.order_number}`
      : (row.order_number ? `Go to order ${row.order_number}` : 'Go to order'),
    disabled: !row.id,
    title: row.is_self_billed ? SELF_BILLED_NO_ORDER_REASON : undefined,
    onClick: () => row.is_haulage_job ? (onNavigate && onNavigate('haulage-jobs')) : setOrderModalId(row.id)
  });

  // Open the table context menu at the cursor for a row. Cmd/Ctrl+right-click is
  // left to the app's radial nav menu; a plain right-click opens this one.
  // Edit the customer order behind a row — same hand-off as Search Customer
  // Orders: stash the id and navigate to the Create/Edit Order screen.
  const handleEditOrder = (orderId) => {
    if (!onNavigate) return;
    sessionStorage.setItem('editOrderId', orderId);
    setOrderModalId(null);
    onNavigate('create-customer-order');
  };

  // Conditional alternating row colour keyed on driver changes: the first band
  // is coloured, it flips off at each driver change, back on at the next, etc.
  const driverBandIds = (() => {
    if (!driverColorsOn) return null;
    const set = new Set();
    let band = 0;
    orders.forEach((o, i) => {
      if (i > 0 && (orders[i].driver || '') !== (orders[i - 1].driver || '')) band++;
      if (band % 2 === 0) set.add(o.order_line_id);
    });
    return set;
  })();

  // ── Row partition + view filters ────────────────────────────────────────
  // Option-date CANDIDATE rows are shown here because this is one of the order's
  // option days, but the order is planned on a different day (not selected here).
  const isCandidateRow = isOptionCandidate;

  // Combined view filters (isolate driver + "planned-with-options only").
  const rowPassesFilters = (o) => {
    if (isolatedDriver !== null) {
      const drv = (o.driver || '').trim();
      const ok = isolatedDriver === '' ? drv === '' : (drv === isolatedDriver || drv === '');
      if (!ok) return false;
    }
    if (showOnlyOptionPlanned && !(o.is_selected_day && o.has_option_dates)) return false;
    return true;
  };

  // Planned rows get a 1..N number (candidates get none); drag needs the row's
  // index within the full `orders` array.
  const plannedNumberById = new Map();
  const indexOfId = new Map();
  let plannedCounter = 0;
  orders.forEach((o, i) => {
    indexOfId.set(o.order_line_id, i);
    if (!isCandidateRow(o)) { plannedCounter += 1; plannedNumberById.set(o.order_line_id, plannedCounter); }
  });
  const plannedCount = plannedCounter;

  const visiblePlanned = orders.filter(o => !isCandidateRow(o) && rowPassesFilters(o));
  const visibleCandidates = orders.filter(o => isCandidateRow(o) && rowPassesFilters(o));

  // One planning-table row. `candidate` rows (option days the order isn't planned
  // on) are greyed, numberless and non-draggable, but otherwise identical.
  const renderPlanningRow = (order, candidate) => {
    const index = indexOfId.get(order.order_line_id);
    const deliveryAddr = formatDeliveryAddress(order);
    const product = formatProduct(order);
    const poTooltip = order.order_number ? `SO: ${order.order_number}` : '';
    const isDragging = !candidate && ((dragBlockRef.current && dragBlockRef.current.includes(order.order_line_id)) || draggedIndex === index);
    return (
      <tr
        key={order.order_line_id}
        draggable={!planFixed && !candidate}
        onDragStart={(!planFixed && !candidate) ? (e) => handleDragStart(e, index) : undefined}
        onDragOver={(!planFixed && !candidate) ? (e) => handleDragOver(e, index) : undefined}
        onDragEnd={(!planFixed && !candidate) ? handleDragEnd : undefined}
        onContextMenu={ctxMenu.onRowContextMenu(order)}
        className={`${isDragging ? 'dragging' : ''} ${selectedRows.has(order.order_line_id) ? 'row-selected' : ''} ${driverBandIds && driverBandIds.has(order.order_line_id) ? 'driver-band' : ''} ${candidate ? 'option-day-row' : ''}`.trim()}
        style={{ color: getStatusColor(order.status), cursor: (planFixed || candidate) ? 'default' : 'move' }}
      >
        <td className="col-select" onClick={(e) => e.stopPropagation()}>
          <label
            className="tick-check-label"
            style={{ justifyContent: 'center', gap: '0' }}
            onMouseDown={dragSelect.onMouseDown(order.order_line_id, selectedRows.has(order.order_line_id))}
            onMouseEnter={dragSelect.onMouseEnter(order.order_line_id)}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          >
            <input
              type="checkbox"
              className="tick-check-input"
              id={`select-tp-${order.order_line_id}`}
              checked={selectedRows.has(order.order_line_id)}
              onChange={() => toggleRowSelected(order)}
            />
            <label htmlFor={`select-tp-${order.order_line_id}`} className="tick-check">
              <svg width="16px" height="16px" viewBox="0 0 18 18">
                <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                <polyline points="1 9 7 14 15 4"></polyline>
              </svg>
            </label>
          </label>
        </td>
        <td className={`col-num ${candidate ? '' : 'drag-handle'}`} style={{ opacity: planFixed ? 0.3 : 1 }} title={candidate ? undefined : 'Drag to reorder'}>
          {!candidate && <span className="col-num-value">{plannedNumberById.get(order.order_line_id)}</span>}
          {order.has_option_dates && (
            <span
              className={`opt-badge ${order.is_selected_day ? 'opt-planned' : 'opt-candidate'}`}
              title={optionTooltip(order)}
            >
              {order.is_selected_day ? 'OPT' : `P${order.option_preference ?? '?'}`}
            </span>
          )}
        </td>
        <td className="col-supplier">
          <div className="supplier-info">
            {order.supplier_name ? (
              <span
                className="supplier-abbr"
                title={`${order.supplier_name}${order.supplier_account_no ? ` (${order.supplier_account_no})` : ''}`}
              >
                <EntityLink type="contact" id={order.supplier_account_id} draggable={false}>
                  {abbreviateSupplier(order.supplier_name)}
                </EntityLink>
              </span>
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </td>
        <td className="col-customer">
          <div className="customer-info">
            <div className="customer-name-row">
              {!order.is_haulage_job && order.customer_account_no && (
                <span className="account-badge">
                  <EntityLink type="contact" id={order.customer_account_id || order.delivery_contact_id} variant="badge" draggable={false}>
                    {order.customer_account_no}
                  </EntityLink>
                </span>
              )}
              <span className="customer-name">
                {/* A self-billed row's "customer" is where its goods go (no account of ours). */}
                <EntityLink type="contact" id={order.customer_account_id || order.delivery_contact_id} draggable={false}>
                  {order.customer_name}
                </EntityLink>
              </span>
            </div>
            {deliveryAddr && (() => {
              const parts = deliveryAddr.split('\n').filter(Boolean);
              const postcode = parts.length > 1 ? parts[parts.length - 1] : '';
              const firstLine = parts[0] || '';
              const hasMore = parts.length > 2 || (parts.length === 2 && postcode !== parts[1]);
              const isExpanded = expandedAddresses.has(order.order_line_id);
              const summary = postcode && postcode !== firstLine ? `${firstLine}, ${postcode}` : firstLine;
              return (
                <div className={`address-line ${isExpanded ? 'address-expanded' : ''}`}>
                  <span className="address-summary">{summary}</span>
                  {hasMore && (
                    <span
                      className="address-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedAddresses(prev => {
                          const next = new Set(prev);
                          next.has(order.order_line_id) ? next.delete(order.order_line_id) : next.add(order.order_line_id);
                          return next;
                        });
                      }}
                    >
                      <span className="material-symbols-rounded">{isExpanded ? 'remove' : 'add'}</span>
                    </span>
                  )}
                  {hasMore && (
                    <div className="address-full">
                      {parts.slice(1).map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </td>
        <td className="col-instructions">
          <span
            className={`notes-info-icon ${orderHasNotes(order) ? 'has-notes' : ''}`}
            title="Customer/ Order Notes"
            onClick={(e) => { e.stopPropagation(); openNotes(order); }}
          >
            <span className="material-symbols-rounded">info</span>
          </span>
          {order.delivery_instructions || <span className="text-muted">—</span>}
          {/* What will physically get down the lane. No label: the lorry icon
              says what it is, and on a planning board every character of width
              is spent on something. */}
          {order.customer_largest_vehicle_access?.trim() && (
            <div className="lva-line" title="Largest vehicle access">
              <span className="material-symbols-rounded">local_shipping</span>
              <span>{order.customer_largest_vehicle_access.trim()}</span>
            </div>
          )}
        </td>
        <td className="col-product">
          <div className="products-info">
            {product ? (
              <div className="product-line">
                {!order.is_haulage_job && product.product_code && (
                  <span className="product-code">
                    <EntityLink type="inventory_item" id={order.inventory_item_id} variant="badge" draggable={false}>
                      {product.product_code}
                    </EntityLink>
                  </span>
                )}
                <span className="product-desc">{product.description}</span>
              </div>
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </td>
        <td className="col-qty">
          {formatQuantity(parseFloat(order.quantity || 0)) || '0.00'}
          {order.line_tonnes != null && (
            <div className="qty-tonnes" title="Estimated delivery weight (from inventory unit weight)">
              {formatTonnes(Number(order.line_tonnes))}
            </div>
          )}
        </td>
        <td className="col-po order-number" title={poTooltip}>
          {/* Stock rows have no PO number — show their sales order number. */}
          <div className="order-number-primary"><RowReferenceLink order={order} /></div>
          {!order.is_haulage_job && (
            <div className="order-number-secondary">{order.supplier_reference || '—'}</div>
          )}
        </td>
        <td className='col-status status-col'>
          <div className="status-row">
            {order.has_changes ? (
              <span
                className="modified-icon"
                title="Order modified - click to view changes"
                onClick={(e) => { e.stopPropagation(); handleShowChanges(order, true); }}
              >
                <span className="material-symbols-rounded">warning</span>
              </span>
            ) : order.has_history ? (
              <span
                className="history-icon"
                title="View change history"
                onClick={(e) => { e.stopPropagation(); handleShowChanges(order, false); }}
              >
                <span className="material-symbols-rounded">history</span>
              </span>
            ) : <span className="history-icon" style={{ visibility: 'hidden' }}><span className="material-symbols-rounded">history</span></span>}
            {(() => {
              // Customer-order rows carry a PO status; haulage-job rows have no PO,
              // so fall back to their delivery status for the badge, and stock
              // rows (also PO-less) are badged as coming off stock.
              const badge = order.is_haulage_job
                ? (order.delivery_status || 'Pending')
                : (order.is_stock_line ? 'Stock' : order.status);
              return badge ? (
                <span className={`status-badge status-${String(badge).toLowerCase().replace(/ /g, '-')}`}>
                  {badge}
                </span>
              ) : null;
            })()}
            {order.is_customer_collection && (
              <span className="status-badge status-customer-collection" title="The customer collects this order — no haulier, driver or vehicle of ours">
                Customer Collection
              </span>
            )}
            {order.third_party_board && (
              <span className="status-badge status-third-party" title={thirdPartyBadgeTitle(order)}>
                Third-party
              </span>
            )}
            {order.is_self_billed && (
              <span className={`status-badge status-self-billed${selfBilledRowPlanned(order) ? '' : ' unplanned'}`}
                title={selfBilledRowPlanned(order) ? SELF_BILLED_BADGE_TITLE : `${SELF_BILLED_BADGE_TITLE} Not planned yet — it will not be on the driver paperwork.`}>
                {SELF_BILLED_LABEL}
              </span>
            )}
            {order.is_redelivery && (
              <span className={`status-badge status-redelivery${redeliveryRowPlanned(order) ? '' : ' unplanned'}`}
                title={`${redeliveryTitle(order.redelivery)}\nCollected from where it is held; never sent to a mill.`}>
                {REDELIVERY_LABEL}
              </span>
            )}
            <span
              className={`price-icon ${order.priced ? 'color-success' : 'color-error'}`}
              title={order.priced ? 'Priced' : 'Not priced'}
              /* Haulage jobs aren't priced here — keep the slot but hide the icon. */
              style={order.is_haulage_job ? { visibility: 'hidden' } : undefined}
            >
              <span className="material-symbols-rounded">verified</span>
            </span>
          </div>
        </td>
        <td className="col-time">
          <div className="time-cell">
            <PlanTimeInput
              className="time-input"
              navRow={order.order_line_id || order.purchase_order_id}
              navCol={0}
              value={order.scheduled_time}
              disabled={planFixed}
              title={planFixed && controlBlock ? controlBlock : undefined}
              ariaLabel={`Collection time, ${order.customer_name || ''}`}
              onCommit={(t) => updateScheduledTime(order.purchase_order_id, t)}
            />
            {/* Drop number within its load — D1 is the first drop. Blank until
                the row is on a vehicle the board can load-check. */}
            <span
              className={`drop-badge${order.run_key ? ' in-run' : ''}`}
              title={dropNumbers.has(order.order_line_id)
                ? `Drop ${dropNumbers.get(order.order_line_id)} of its load${order.run_key ? ' (combined run)' : ''}`
                : 'No drop number until the row is on a vehicle'}
            >
              {dropNumbers.has(order.order_line_id) ? `D${dropNumbers.get(order.order_line_id)}` : ''}
            </span>
          </div>
        </td>
        <td className="col-haulier">
          <SearchableDropdown
            options={[
              { value: '', label: 'None' },
              ...hauliers.map(h => ({ value: h, label: h })),
              // The row's own haulier even when it is not in the list — a rep
              // cannot load the list (edit permission), and an archived haulier
              // is not in it; either way the cell showed blank. (Driver already
              // did this.)
              ...(order.haulier && !hauliers.includes(order.haulier) ? [{ value: order.haulier, label: order.haulier }] : [])
            ]}
            value={order.haulier || ''}
            onChange={(value) => updateHaulier(order.order_line_id, value)}
            placeholder="Select..."
            searchPlaceholder="Search..."
            disabled={cellsFixed || order.is_customer_collection}
          />
        </td>
        <td className="col-driver">
          {(() => {
            const filtered = order.haulier
              ? drivers.filter(d => (d.hauliers || []).some(h => h.name === order.haulier))
              : drivers;
            const names = new Set(filtered.map(d => d.name));
            if (order.driver && !names.has(order.driver)) names.add(order.driver);
            const opts = [
              { value: '', label: 'None' },
              ...Array.from(names).sort().map(n => ({ value: n, label: n }))
            ];
            return (
              <SearchableDropdown
                options={opts}
                value={order.driver || ''}
                onChange={(value) => updateDriver(order.order_line_id, value)}
                placeholder="Select..."
                searchPlaceholder="Search..."
                disabled={cellsFixed || order.is_customer_collection}
              />
            );
          })()}
        </td>
        <td className="col-vehicle">
          <Dropdown
            options={[
              { value: '', label: 'None' },
              { value: UNKNOWN_VEHICLE, label: 'Unknown (set payload)' },
              ...vehicles.map(v => ({ value: v, label: v })),
              // As for haulier: always offer the row's own vehicle.
              ...(order.vehicle && order.vehicle !== UNKNOWN_VEHICLE && !vehicles.includes(order.vehicle) ? [{ value: order.vehicle, label: order.vehicle }] : [])
            ]}
            value={order.vehicle || ''}
            onChange={(value) => updateVehicle(order.order_line_id, value)}
            placeholder="Select..."
            searchPlaceholder="Search..."
            disabled={cellsFixed || order.is_customer_collection}
          />
        </td>
        {/* The row's three-dots menu: the same row actions as right-click
            for go-to-order and archiving. */}
        <td className="col-actions" onClick={(e) => e.stopPropagation()}>
          <div className="actions-cell">
            <RowActionsMenu actions={[goToOrderAction(order), ...rowRedeliver.actionsFor(order), ...rowArchive.actionsFor(order)]} />
          </div>
        </td>
      </tr>
    );
  };

  // ── Drag auto-scroll (enlarged trigger zone, eased + gentle speed) ──────
  const runAutoScroll = () => {
    const el = transportScrollRef.current;
    const v = autoScrollRef.current.velocity;
    if (!el || v === 0) { autoScrollRef.current.raf = null; return; }
    el.scrollTop += v;
    autoScrollRef.current.raf = requestAnimationFrame(runAutoScroll);
  };

  const updateAutoScroll = (clientY) => {
    const el = transportScrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ZONE = 130;      // px — enlarged trigger band top & bottom
    const MAX_SPEED = 6;   // px/frame (~360px/s at 60fps) — gentle + smooth
    let velocity = 0;
    if (clientY < rect.top + ZONE) {
      const intensity = Math.min(1, (rect.top + ZONE - clientY) / ZONE);
      velocity = -MAX_SPEED * intensity;
    } else if (clientY > rect.bottom - ZONE) {
      const intensity = Math.min(1, (clientY - (rect.bottom - ZONE)) / ZONE);
      velocity = MAX_SPEED * intensity;
    }
    autoScrollRef.current.velocity = velocity;
    if (velocity !== 0 && !autoScrollRef.current.raf) {
      autoScrollRef.current.raf = requestAnimationFrame(runAutoScroll);
    }
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current.raf) cancelAnimationFrame(autoScrollRef.current.raf);
    autoScrollRef.current.raf = null;
    autoScrollRef.current.velocity = 0;
  };

  // ── Drag & drop (single row, or a multi-selected block moved together) ──
  const handleDragStart = (e, index) => {
    // A drag starting on the checkbox cell is a select-paint, not a row move.
    if (e.target.closest && e.target.closest('.col-select')) {
      e.preventDefault();
      return;
    }
    const row = orders[index];
    // If the dragged row is part of a multi-selection, drag the whole block.
    dragBlockRef.current = (selectedRows.has(row.order_line_id) && selectedRows.size > 1)
      ? orders.filter(o => selectedRows.has(o.order_line_id)).map(o => o.order_line_id)
      : [row.order_line_id];
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    updateAutoScroll(e.clientY);

    const block = dragBlockRef.current;
    if (!block || block.length === 0) return;
    const blockSet = new Set(block);
    const hoveredRow = orders[index];
    // Hovering over the block itself → nothing to do (prevents jitter).
    if (!hoveredRow || blockSet.has(hoveredRow.order_line_id)) return;

    const blockRows = orders.filter(o => blockSet.has(o.order_line_id));
    const rest = orders.filter(o => !blockSet.has(o.order_line_id));
    const rIdx = rest.findIndex(o => o.order_line_id === hoveredRow.order_line_id);
    if (rIdx < 0) return;
    // Drop the block after the hovered row when dragging down, before it when
    // dragging up — so its first row lands on the hovered slot.
    const firstBlockIdx = orders.findIndex(o => o.order_line_id === block[0]);
    const insertAt = firstBlockIdx < index ? rIdx + 1 : rIdx;
    const newOrders = [...rest.slice(0, insertAt), ...blockRows, ...rest.slice(insertAt)];

    if (newOrders.some((o, i) => o.order_line_id !== orders[i].order_line_id)) {
      setOrders(newOrders);
      setDraggedIndex(newOrders.findIndex(o => o.order_line_id === block[0]));
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    dragBlockRef.current = null;
    stopAutoScroll();
    // Use ref to get latest orders (avoids stale closure from async state updates)
    updateOrderSequence(ordersRef.current);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${dayNames[date.getDay()]}\n${date.getDate()}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PO Confirmed':
        return 'var(--text-primary)';
      case 'PO Created':
        return 'var(--color-primary)';
      case 'PO Sent':
        return 'var(--color-error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const formatDeliveryAddress = (order) => {
    // Countries/regions to exclude
    const excludeCountries = ['united kingdom', 'gb', 'gbr', 'uk', 'england', 'wales', 'scotland', 'great britain'];
    const customerNameLower = (order.customer_name || '').toLowerCase().trim();

    // Helper to filter address parts
    const filterPart = (p) => {
      if (!p || (typeof p === 'string' && !p.trim())) return false;
      if (typeof p !== 'string') return false;
      const pLower = p.toLowerCase().trim();
      // Skip if it matches customer name
      if (customerNameLower && pLower === customerNameLower) return false;
      // Skip country values
      if (excludeCountries.includes(pLower)) return false;
      return true;
    };

    // Helper to normalize address object keys (handles both camelCase and snake_case)
    const normalizeAddress = (addr) => {
      if (!addr || typeof addr !== 'object') return null;
      return {
        attentionTo: addr.attentionTo || addr.attention_to || addr.AttentionTo || '',
        addressLine1: addr.addressLine1 || addr.address_line_1 || addr.AddressLine1 || addr.line1 || '',
        addressLine2: addr.addressLine2 || addr.address_line_2 || addr.AddressLine2 || addr.line2 || '',
        addressLine3: addr.addressLine3 || addr.address_line_3 || addr.AddressLine3 || addr.line3 || '',
        addressLine4: addr.addressLine4 || addr.address_line_4 || addr.AddressLine4 || addr.line4 || '',
        city: addr.city || addr.City || addr.town || addr.Town || '',
        region: addr.region || addr.Region || addr.county || addr.County || addr.state || addr.State || '',
        postalCode: addr.postalCode || addr.postal_code || addr.PostalCode || addr.postcode || addr.Postcode || addr.zipCode || addr.zip || ''
      };
    };

    // Helper to build full address from address object
    const buildFullAddress = (addr, suffix = '') => {
      const normalized = normalizeAddress(addr);
      if (!normalized) return '';

      const parts = [
        normalized.attentionTo,
        normalized.addressLine1,
        normalized.addressLine2,
        normalized.addressLine3,
        normalized.addressLine4,
        normalized.city,
        normalized.region,
        normalized.postalCode
      ].filter(filterPart);

      if (parts.length === 0) return '';
      const fullAddr = parts.join('\n');
      return suffix ? `${fullAddr} ${suffix}` : fullAddr;
    };

    // Helper to safely parse JSON, returning null on failure
    const safeParseJson = (str) => {
      if (!str) return null;
      if (typeof str !== 'string') return str; // Already an object
      try {
        const parsed = JSON.parse(str);
        // Handle double-encoded JSON strings
        if (typeof parsed === 'string') {
          try {
            return JSON.parse(parsed);
          } catch (e) {
            // It was a JSON string containing a plain string address
            return parsed;
          }
        }
        return parsed;
      } catch (e) {
        return null; // Not valid JSON
      }
    };

    // Helper to check if something looks like an address object
    const isAddressObject = (obj) => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
      // Check for common address keys
      const addressKeys = ['addressLine1', 'address_line_1', 'AddressLine1', 'line1', 'city', 'City', 'postcode', 'postalCode', 'postal_code'];
      return addressKeys.some(key => obj[key]);
    };

    // Helper to clean a string address (handles newline or comma-separated addresses)
    const cleanStringAddress = (str, suffix = '') => {
      if (!str || typeof str !== 'string') return '';
      // Split by newlines first, then by commas, and filter parts (removes customer name, country, etc.)
      const parts = str
        .split(/[\n\r]+/)
        .flatMap(line => line.split(','))
        .map(p => p.trim())
        .filter(filterPart);
      if (parts.length === 0) return '';
      const result = parts.join('\n');
      return suffix ? `${result} ${suffix}` : result;
    };

    // First try PO delivery address
    if (order.delivery_address) {
      const parsed = safeParseJson(order.delivery_address);

      if (parsed && isAddressObject(parsed)) {
        const result = buildFullAddress(parsed);
        if (result) return result;
      } else if (typeof parsed === 'string' && parsed.trim()) {
        // It was a JSON-encoded plain string address (often newline-separated from auto-generated POs)
        const cleaned = cleanStringAddress(parsed);
        if (cleaned) return cleaned;
      } else if (typeof order.delivery_address === 'string' && order.delivery_address.trim()) {
        // Plain string that wasn't valid JSON - clean and use it
        const cleaned = cleanStringAddress(order.delivery_address);
        if (cleaned) return cleaned;
      }
    }

    // Try to get delivery address from flat customer columns
    if (order.customer_delivery_line1) {
      const addrObj = {
        addressLine1: order.customer_delivery_line1,
        addressLine2: order.customer_delivery_line2,
        addressLine3: order.customer_delivery_line3,
        addressLine4: order.customer_delivery_line4,
        city: order.customer_delivery_city,
        region: order.customer_delivery_region,
        postalCode: order.customer_delivery_postcode,
        country: order.customer_delivery_country
      };
      const result = buildFullAddress(addrObj);
      if (result) return result;
    }

    // Fallback to customer invoice address (from address column)
    if (order.customer_invoice_address) {
      const parsed = safeParseJson(order.customer_invoice_address);

      if (parsed && isAddressObject(parsed)) {
        const result = buildFullAddress(parsed, '(INV)');
        if (result) return result;
      } else if (typeof parsed === 'string' && parsed.trim()) {
        const cleaned = cleanStringAddress(parsed, '(INV)');
        if (cleaned) return cleaned;
      } else if (typeof order.customer_invoice_address === 'string' && order.customer_invoice_address.trim()) {
        const cleaned = cleanStringAddress(order.customer_invoice_address, '(INV)');
        if (cleaned) return cleaned;
      }
    }

    return '';
  };

  const formatProduct = (order) => {
    // Each row now represents a single order line
    if (!order.product_code && !order.product_name) {
      return null;
    }
    return {
      product_code: order.product_code,
      description: order.product_name
    };
  };

  // Day-level rollup shown above the vehicle load cards.
  const daySummary = useMemo(() => {
    // Customer Collection orders stay on the board but are no delivery of
    // ours, so they are left out of every count here (Cam, 2026-10-02).
    // So is a self-billed PO someone else carries (utils/selfBilledPo).
    const orders = ourDeliveryRows(boardOrders);
    const distinct = (vals) => new Set(vals.filter(v => v && String(v).trim())).size;
    const totalTonnes = orders.reduce((sum, o) => {
      const t = Number(o.line_tonnes);
      return sum + (Number.isFinite(t) ? t : (Number(o.quantity) || 0));
    }, 0);
    // Distinct delivery destinations — key on the formatted address, falling
    // back to the customer name when no address is present.
    const addressKeys = orders.map(o => {
      const a = formatDeliveryAddress(o);
      return (a ? a.replace(/\s+/g, ' ').trim().toLowerCase() : (o.customer_name || '').toLowerCase());
    }).filter(Boolean);
    // "Our" = the Excel Agri haulier; everything else is 3rd-party.
    const isExcel = (o) => (o.haulier || '').trim().toLowerCase() === 'excel agri';
    return {
      orders: orders.length,
      totalTonnes,
      addresses: new Set(addressKeys).size,
      suppliers: distinct(orders.map(o => o.supplier_name || o.contact_name)),
      ourVehicles: distinct(orders.filter(isExcel).map(o => o.vehicle)),
      thirdPartyDrivers: distinct(orders.filter(o => !isExcel(o)).map(o => o.driver))
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardOrders]);

  const formatAutoSaveTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return 'Just now';
    if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Validate and prepare driver paperwork
  const handleDriverPaperwork = () => {
    const warnings = [];

    // Customer Collection: no haulier or time of ours to check. An unplanned
    // self-billed PO is not missing anything — it never needs planning, and
    // is left out of the paperwork (the send window says so).
    driverPaperworkRows(ourDeliveryRows(orders)).forEach(order => {
      const issues = [];
      if (!order.haulier) issues.push('no haulier');
      if (!order.scheduled_time) issues.push('no time');
      if (!order.quantity || order.quantity === 0) issues.push('0 quantity');
      if (!order.delivery_address && !order.customer_name) issues.push('no delivery address');
      if (!order.supplier_name && !order.contact_name) issues.push('no supplier');

      if (issues.length > 0) {
        warnings.push({
          po_number: rowReference(order),
          // Carried so the warning rows can link the same way the board does.
          order,
          customer_account_no: order.customer_account_no,
          customer_account_id: order.customer_account_id,
          customer: order.customer_name || 'Unknown',
          product_code: order.product_code,
          inventory_item_id: order.inventory_item_id,
          product: order.product_name || 'Unknown',
          quantity: order.quantity,
          issues
        });
      }
    });

    setPaperworkModal({ open: true, warnings, generating: false, progress: 0 });
  };

  // Unlock with the shared transport unlock password — checked by the server
  // (PUT /lock), which also re-locks a past day after the correction window.
  const handleUnlock = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transport/lock/${selectedDate}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isLocked: false, password: unlockModal.password })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setIsLocked(data.isLocked);
        setUnlockedUntil(data.unlockedUntil || null);
        setUnlockModal({ open: false, password: '', error: null });
      } else {
        setUnlockModal(prev => ({ ...prev, error: data.error || 'Could not unlock the day' }));
      }
    } catch (error) {
      console.error('Unlock failed:', error);
      setUnlockModal(prev => ({ ...prev, error: 'Could not reach the server to unlock the day' }));
    }
  };

  // A past day re-locks itself when its correction window ends; follow it.
  useEffect(() => {
    if (!unlockedUntil) return undefined;
    const ms = new Date(unlockedUntil).getTime() - Date.now();
    const timer = setTimeout(() => fetchLockStatus(selectedDate, isPastDate), Math.max(ms, 0) + 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockedUntil, selectedDate]);

  // Open driver selection modal after paperwork validation.
  // Paperwork is grouped by DRIVER — each driver gets one email with only their
  // orders. Rows with no driver assigned fall back to a haulier (or "Unassigned")
  // group so they still get sent. Suppliers/mills are never a grouping boundary.
  const openHaulierSelection = () => {
    setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 });

    // One row per driver (else haulier / Unassigned); the address is the
    // driver's own, else their haulier's — a fallback the window says out
    // loud (utils/driverPaperworkRecipients, shared with the other board).
    // Customer Collection orders are in no driver's paperwork, nor is an
    // unplanned self-billed PO (utils/selfBilledPo).
    const list = buildDriverPaperworkRecipients(driverPaperworkRows(ourDeliveryRows(orders)), drivers, hauliersWithEmail);

    setHaulierSelectionModal({ open: true, hauliers: list, sendToOffice: false, copyToOffice: false });
  };

  const toggleHaulier = (key, include) => setHaulierSelectionModal(prev => ({
    ...prev, hauliers: prev.hauliers.map(h => h.key === key ? { ...h, include } : h)
  }));

  // "Add <name>'s email" in the send window: saved to the driver (checked
  // and audited by the server), so this pack — and every one after it —
  // goes to them directly instead of to their haulier's address.
  const saveRecipientEmail = async (key, email) => {
    const row = haulierSelectionModal.hauliers.find(h => h.key === key);
    if (!row?.driverId) throw new Error('This driver is not in Transport Settings → Drivers');
    const saved = await saveDriverEmail(row.driverId, email);
    setDrivers(prev => prev.map(d => (d.id === saved?.id ? { ...d, ...saved } : d)));
    setHaulierSelectionModal(prev => ({ ...prev, hauliers: withDriverEmail(prev.hauliers, key, saved?.email || email) }));
  };


  // Generate and send driver paperwork
  // `notice` comes from PaperworkSendModal: shown under the greeting of each email.
  const generatePaperwork = async ({ notice = '', sendAs = 'admin' } = {}) => {
    const { sendToOffice, copyToOffice } = haulierSelectionModal;
    const selectedHauliers = haulierSelectionModal.hauliers.filter(h => h.include && (sendToOffice || h.email));

    if (selectedHauliers.length === 0) {
      setAlertDialog({
        open: true,
        title: 'No Hauliers Selected',
        message: 'Please select at least one haulier to send paperwork to.',
        type: 'warning'
      });
      return;
    }

    // Check all selected hauliers have email addresses
    setHaulierSelectionModal(prev => ({ ...prev, generating: true }));

    try {
      // Build driver-group selections for the API (keyed by the group key).
      const driverSelections = selectedHauliers.map(h => ({
        key: h.key,
        email: h.email,
        include: true
      }));

      const response = await fetch(`${API_URL}/api/transport/planning/generate-paperwork`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          orders: driverPaperworkRows(ourDeliveryRows(orders)),
          driverSelections,
          sendToOffice,
          // Cc the office on each driver's own email (never instead of them).
          copyToOffice: !sendToOffice && !!copyToOffice,
          notice,
          sendAs
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate paperwork');
      }

      const result = await response.json();

      // Close modal
      setHaulierSelectionModal({ open: false, hauliers: [] });

      // Refresh paperwork log
      fetchPaperworkLog(selectedDate);

      // Show success message
      const successCount = result.successCount || result.haulierCount;
      const failedCount = result.failedCount || 0;

      if (failedCount > 0) {
        const failedHauliers = result.emailResults
          ?.filter(r => !r.success)
          .map(r => `${r.haulier}: ${r.error}`)
          .join('\n') || '';

        setAlertDialog({
          open: true,
          title: 'Driver Paperwork Partially Sent',
          message: `Sent to ${successCount} driver(s), ${failedCount} failed.\n\nFailed:\n${failedHauliers}`,
          type: 'warning'
        });
      } else {
        setAlertDialog({
          open: true,
          title: 'Driver Paperwork Sent',
          message: `Driver Paperwork sent successfully to ${successCount} driver(s).`,
          type: 'success'
        });
      }

    } catch (error) {
      console.error('Failed to generate driver paperwork:', error);
      setHaulierSelectionModal(prev => ({ ...prev, generating: false }));
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Failed to generate driver paperwork: ${error.message}`,
        type: 'error'
      });
    }
  };

  // ── Office summary (full-colour day summary PDF emailed to the office) ──
  const openOfficeSummary = () => {
    setOfficeSummaryModal({ open: true, generating: false });
  };

  // Build the pre-formatted payload the server renders into the summary PDF.
  // The rows are grouped by haulier · driver · vehicle as the bulk board
  // groups them (utils/boardGroups): not yet assigned first, then our lorries
  // by first collection, then outside hauliers'; each lorry's rows by
  // collection time (a run at its first time), then board order, with each
  // row's D#. `groups` carries them under their headings; `deliveries` is the
  // same rows flat, in the same order (the counts, and a server from before).
  const buildOfficeSummaryPayload = () => {
    // Our deliveries only: Customer Collection orders are left out.
    const orders = ourDeliveryRows(boardOrders);
    const RING_CIRC = 125.66; // 2πr for r=20, matches transport-summary.css
    const fitLabelFor = (fit) => (
      fit === 'ok' ? 'Fits'
        : fit === 'too-many' ? 'Too many drops'
        : fit === 'wont-fit' ? "Won't fit"
        : fit === 'reorder' ? 'Re-sequence'
        : 'No compartment spec'
    );

    const toDelivery = (o) => {
      const addr = formatDeliveryAddress(o);
      return {
        supplierAccount: o.supplier_account_no || '',
        supplierName: o.supplier_name || o.contact_name || '',
        customerAccount: o.customer_account_no || '',
        customerName: o.customer_name || '',
        deliveryAddress: addr ? addr.split('\n').filter(Boolean).join(', ') : '',
        // The office summary's Del. Inst. cell carries everything a driver or
        // the office needs to know: the delivery instructions (the order's,
        // with the PO's own text — utils/planningDay; they used to be printed
        // twice, as instructions and as "Order notes"), largest vehicle access
        // and the customer's notes.
        deliveryInstructions: (o.delivery_instructions || '').trim(),
        lva: (o.customer_largest_vehicle_access || '').trim(),
        customerNotes: (o.customer_notes || '').trim(),
        hasInstructions: !!((o.delivery_instructions || '').trim() || (o.customer_largest_vehicle_access || '').trim()
          || (o.customer_notes || '').trim()),
        productCode: o.product_code || '',
        productName: o.product_name || '',
        quantity: formatQuantity(parseFloat(o.quantity || 0)) || '0.00',
        lineTonnes: o.line_tonnes != null ? formatQuantity(Number(o.line_tonnes)) : '',
        poNumber: rowReference(o),
        supplierRef: o.supplier_reference || '',
        status: o.is_stock_line ? 'Stock' : (o.status || ''),
        statusClass: (o.is_stock_line ? 'stock' : (o.status || '')).toLowerCase().replace(/ /g, '-'),
        scheduledTime: o.scheduled_time ? String(o.scheduled_time).slice(0, 5) : '',
        haulier: o.haulier || '',
        driver: o.driver || '',
        vehicle: o.vehicle || ''
      };
    };
    // The bag board has no lorry day plan: D# is the row's drop within its load.
    const { groups, deliveries } = officeSummaryLayout(orders, {
      toDelivery,
      newRules: false,
      dropNumberOf: (o) => dropNumbers.get(o.order_line_id)
    });

    const vlPayload = vehicleLoads.map((l) => {
      const effSev = efficiencySeverity(l);
      const fitSev = fitSeverity(l);
      const over = l.effPayload != null && l.totalTonnes > l.effPayload;
      const pct = l.effPayload ? Math.min(100, (l.totalTonnes / l.effPayload) * 100) : 0;
      const eff = l.efficiency != null ? Math.round(l.efficiency) : null;
      const uFitSev = l.isUnknown ? (l.effPayload == null ? 'warn' : (over ? 'danger' : 'ok')) : fitSev;
      const uFitLabel = l.isUnknown
        ? (l.effPayload == null ? 'Set payload' : (over ? 'Over payload' : 'Payload set'))
        : fitLabelFor(l.fit);
      return {
        registration: l.registration,
        multiLoad: l.loadCount > 1,
        loadLabel: `${l.loadIndex + 1}/${l.loadCount}`,
        fitSeverity: uFitSev,
        fitLabel: uFitLabel,
        effSeverity: effSev,
        hasEfficiency: eff != null,
        efficiency: eff,
        ringOffset: eff != null
          ? (RING_CIRC * (1 - Math.min(100, Math.max(0, eff)) / 100)).toFixed(2)
          : RING_CIRC,
        over,
        pct: Math.round(pct),
        totalTonnes: formatQuantity(l.totalTonnes),
        payloadLabel: l.effPayload != null ? formatTonnes(l.effPayload) : 'payload n/a',
        moffettLabel: (l.hasMoffett && l.effPayload != null) ? ' (−1 Moffett)' : '',
        dropLabel: `${l.lineCount} drop${l.lineCount === 1 ? '' : 's'}`,
        supplierLine: `${l.supplier || ''}${l.timeLabel ? ` · ${l.timeLabel}` : ''}`,
        driver: l.driver || null,
        overMsg: over ? `Over payload by ${formatTonnes(l.overBy)}` : '',
        messages: Array.isArray(l.messages) ? l.messages : [],
        doorMsg: (l.doorConfig && l.fit !== 'wont-fit')
          ? `Set doors at: ${l.doorConfig.map(d => `${d}T`).join(', ')}`
          : ''
      };
    });

    const totalTonnes = orders.reduce((sum, o) => {
      const t = Number(o.line_tonnes);
      return sum + (Number.isFinite(t) ? t : (Number(o.quantity) || 0));
    }, 0);
    const distinct = (vals) => new Set(vals.filter(v => v && String(v).trim())).size;

    return {
      deliveries,
      groups,
      dayRules: 'old',
      vehicleLoads: vlPayload,
      stats: {
        deliveryCount: orders.length,
        totalTonnes: formatQuantity(totalTonnes),
        loadCount: vehicleLoads.length,
        vehicleCount: distinct(orders.map(o => o.vehicle)),
        driverCount: distinct(orders.map(o => o.driver)),
        haulierCount: distinct(orders.map(o => o.haulier))
      }
    };
  };

  // Recipients come from OfficeSummaryModal: the office, the ticked reps,
  // and anyone added.
  const sendOfficeSummary = async (recipients, { notice = '', sendAs = 'admin' } = {}) => {
    // De-dupe case-insensitively, preserving first-seen casing
    const unique = [...new Map(recipients.map(e => [e.toLowerCase(), e])).values()];

    if (unique.length === 0) {
      setAlertDialog({
        open: true,
        title: 'No Recipients',
        message: 'Please select at least one recipient for the summary.',
        type: 'warning'
      });
      return;
    }

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const bad = unique.filter(e => !emailRe.test(e));
    if (bad.length > 0) {
      setAlertDialog({
        open: true,
        title: 'Invalid Email Address',
        message: `These addresses look invalid:\n${bad.join('\n')}`,
        type: 'warning'
      });
      return;
    }

    setOfficeSummaryModal(prev => ({ ...prev, generating: true }));

    try {
      const response = await fetch(`${API_URL}/api/transport/planning/send-office-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          recipients: unique,
          summary: buildOfficeSummaryPayload(),
          notice,
          sendAs
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send summary');
      }

      setOfficeSummaryModal({ open: false, generating: false });
      fetchOfficeSummaryLog(selectedDate);
      setAlertDialog({
        open: true,
        title: 'Summary Sent',
        message: `Transport summary sent successfully to ${unique.length} recipient(s).`,
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to send office summary:', error);
      setOfficeSummaryModal(prev => ({ ...prev, generating: false }));
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Failed to send summary: ${error.message}`,
        type: 'error'
      });
    }
  };

  // Open supplier selection modal
  const openSupplierSelection = () => {
    // Group orders by supplier and get email from order data. A self-billed PO
    // never goes to a mill (utils/selfBilledPo).
    const supplierData = {};
    millEmailRows(orders).forEach(order => {
      // Stock rows come off our own stock — there's no supplier to notify.
      if (order.is_stock_line) return;
      const supplier = order.supplier_name || order.contact_name || 'Unknown';
      if (!supplierData[supplier]) {
        supplierData[supplier] = {
          orderCount: 0,
          email: order.supplier_email || '',
          accountId: order.supplier_account_id || null
        };
      }
      if (!supplierData[supplier].accountId && order.supplier_account_id) {
        supplierData[supplier].accountId = order.supplier_account_id;
      }
      supplierData[supplier].orderCount += 1;
      // Use the first non-empty email found for this supplier
      if (!supplierData[supplier].email && order.supplier_email) {
        supplierData[supplier].email = order.supplier_email;
      }
    });

    // Build supplier selection list
    const supplierList = Object.entries(supplierData).map(([name, data]) => ({
      name,
      key: name,
      orderCount: data.orderCount,
      accountId: data.accountId,
      email: data.email,
      emails: data.email ? data.email.split(/[;,]/).map(e => e.trim()).filter(Boolean) : [],
      include: true
    }));

    // Sort by name
    supplierList.sort((a, b) => a.name.localeCompare(b.name));

    setSupplierSelectionModal({ open: true, suppliers: supplierList, sendToOffice: false });

    // The addresses the send will actually use (Transport Settings → Mill
    // Emails, else the contact record) — shown read-only.
    const ids = supplierList.map(x => x.accountId).filter(Boolean);
    fetch(`${API_URL}/api/transport/planning/paperwork-recipients?accountIds=${ids.join(',')}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.officeEmail) setOfficeEmail(d.officeEmail);
        setSupplierSelectionModal(prev => ({
          ...prev,
          suppliers: prev.suppliers.map(x => {
            const r = x.accountId && d.suppliers?.[x.accountId];
            if (!r || !r.emails?.length) return x;
            return { ...x, emails: r.emails, emailNote: r.source === 'paperwork' ? null : 'From the contact record — set paperwork addresses in Transport Settings → Mill Emails' };
          })
        }));
      })
      .catch(() => {});
  };

  const toggleSupplier = (key, include) => setSupplierSelectionModal(prev => ({
    ...prev, suppliers: prev.suppliers.map(x => x.key === key ? { ...x, include } : x)
  }));

  // Send supplier emails
  const sendSupplierEmails = async ({ notice = '', sendAs = 'admin' } = {}) => {
    const { sendToOffice } = supplierSelectionModal;
    const selectedSuppliers = supplierSelectionModal.suppliers.filter(s => s.include && (sendToOffice || s.emails.length));

    if (selectedSuppliers.length === 0) {
      setAlertDialog({
        open: true,
        title: 'No Suppliers Selected',
        message: 'Please select at least one supplier to send emails to.',
        type: 'warning'
      });
      return;
    }


    setSupplierSelectionModal(prev => ({ ...prev, generating: true }));

    try {
      // Build supplier selections for API
      const supplierSelections = selectedSuppliers.map(s => ({
        supplier: s.name,
        email: s.emails.join(', '),
        include: true
      }));

      const response = await fetch(`${API_URL}/api/transport/planning/send-supplier-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          // Each row carries the compartments it loads into ("3, 4", from
          // the load analysis) for the mills that want them — see COMP in
          // routes/transport.js send-supplier-emails.
          orders: millEmailRows(orders).map(o => ({ ...o, compartments: compartmentsFor.get(o.order_line_id) || '' })),
          supplierSelections,
          sendToOffice,
          notice,
          sendAs
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send supplier emails');
      }

      const result = await response.json();

      // Close modal
      setSupplierSelectionModal({ open: false, suppliers: [] });

      // Refresh supplier email log
      fetchSupplierEmailLog(selectedDate);

      // Show success message
      const successCount = result.successCount || result.supplierCount;
      const failedCount = result.failedCount || 0;

      if (failedCount > 0) {
        const failedSuppliers = result.emailResults
          ?.filter(r => !r.success)
          .map(r => `${r.supplier}: ${r.error}`)
          .join('\n') || '';

        setAlertDialog({
          open: true,
          title: 'Emails Partially Sent',
          message: `Sent to ${successCount} supplier(s), ${failedCount} failed.\n\nFailed:\n${failedSuppliers}`,
          type: 'warning'
        });
      } else {
        setAlertDialog({
          open: true,
          title: 'Supplier Emails Sent',
          message: `Order summaries sent successfully to ${successCount} supplier(s).`,
          type: 'success'
        });
      }

    } catch (error) {
      console.error('Failed to send supplier emails:', error);
      setSupplierSelectionModal(prev => ({ ...prev, generating: false }));
      setAlertDialog({
        open: true,
        title: 'Error',
        message: `Failed to send supplier emails: ${error.message}`,
        type: 'error'
      });
    }
  };

  const todayStr = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  })();

  return (
    <div className={`transport-planning${canEdit ? '' : ' read-only-mode'}`}>
      <PlanningControlBar control={control} board="bag" />
      {!canPlan && (
        <div
          style={{
            background: 'var(--bg-secondary, #f5f5f5)',
            border: '1px solid var(--color-warning, #f59e0b)',
            borderRadius: '6px',
            padding: '10px 14px',
            margin: '0 0 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.9rem'
          }}
        >
          <span className="material-symbols-rounded" style={{ color: 'var(--color-warning, #f59e0b)' }}>visibility</span>
          <span>
            <strong>Read-only.</strong> You don't have permission to edit Transport Planning.
            Ask an admin to grant <em>Transport Planning</em> in User Management if you need to make changes.
          </span>
        </div>
      )}
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Plan and optimise transport routes and schedules</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Coordinate deliveries and manage transport for bag orders and haulage jobs.
            </span>
          </span>
        </div>
        
        <div className="search-header-inner">
          <div className="notifications">
            {lastAutoSaved && (
              <HeaderNotice variant="notice-save" icon="check_circle">
                <span className="sent-time"><span>Last auto-saved</span> {formatAutoSaveTime(lastAutoSaved)}</span>
              </HeaderNotice>
            )}
            <TransportEmailNotices
              date={selectedDate}
              supplierLog={supplierEmailLog}
              paperworkLog={paperworkLog}
              officeLog={officeSummaryLog}
              onOpenSends={(kind) => setHistoryModal({ open: true, kind })}
              onOpenPlanHistory={openPlanHistory}
              canSeeHistory={badgeOf(user) !== 'REP'}
            />
          </div>
          <div className="buttons">
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: lockLoading || controlBlock ? 'default' : 'pointer', opacity: controlBlock ? 0.5 : 1 }}
              title={controlBlock || (isLocked
                ? (isPastDate ? 'Unlock this past day to correct haulier, driver or vehicle (needs the unlock password)' : 'Unlock planning (needs the unlock password)')
                : 'Lock planning')}
              onClick={() => {
                if (lockLoading || controlBlock) return;
                if (isLocked) {
                  setUnlockModal({ open: true, password: '', error: null });
                } else {
                  saveLockStatus(selectedDate, true);
                }
              }}
            >
              <div className="check danger light" style={{ fontSize: '22px', pointerEvents: 'none' }}>
                <input
                  type="checkbox"
                  checked={isLocked}
                  readOnly
                />
                <label></label>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', userSelect: 'none', fontSize: '0.9em', marginRight: '10px' }}>
                <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>
                  {isLocked ? 'lock' : 'lock_open'}
                </span>
                {isLocked ? 'Locked'
                  : (isPastDate && unlockedUntil
                    ? `Correcting until ${new Date(unlockedUntil).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                    : 'Unlocked')}
              </span>
            </div>
            <button
              onClick={openSupplierSelection}
              className="btn-slideout"
              disabled={planFixed || orders.length === 0}
              title="Email order summaries to supplier(s)/ mill(s)"
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">assignment_turned_in</span>
              </div>
              <span className="btn-label">Suppliers/ Mills</span>
            </button>
            <button
              onClick={handleDriverPaperwork}
              className="btn-slideout"
              disabled={planFixed || orders.length === 0}
              title="Generate driver paperwork"
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">local_shipping</span>
              </div>
              <span className="btn-label">Driver Paperwork</span>
            </button>
            <button
              onClick={openOfficeSummary}
              className="btn-slideout"
              disabled={orders.length === 0}
              title="Email a full transport planning summary to the office"
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">groups</span>
              </div>
              <span className="btn-label">Send to Office</span>
            </button>
            <button
              onClick={() => openPlanHistory('email')}
              className={`btn-slideout${badgeOf(user) === 'REP' ? ' btn-disabled' : ''}`}
              disabled={badgeOf(user) === 'REP'}
              data-read-safe
              title={badgeOf(user) === 'REP'
                ? 'Plan history covers every customer on the day, so is not available to rep accounts'
                : 'Everything that happened to this day\'s plan: changes, moves, times, locks and emails sent'}
            >
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">history</span>
              </div>
              <span className="btn-label">Plan History</span>
            </button>
          </div>
        </div>
        <div className={`snapshot-banner-wrapper ${isSnapshot ? 'visible' : ''}`}>
          <div className="snapshot-banner">
            <span className="material-symbols-rounded">info</span>
            <span>
              You are viewing a snapshot of the transport plan for this day. For actual delivered and supplied quantities, check individual customer orders.
            </span>
          </div>
        </div>
      </div>

      {/* Date Selector (collapsible) */}
      <button
        className="tp-section-toggle date-picker-toggle"
        onClick={() => setDatePickerCollapsed(c => !c)}
        title={datePickerCollapsed ? 'Show date picker' : 'Hide date picker'}
      >
        <span className="material-symbols-rounded">{datePickerCollapsed ? 'chevron_right' : 'expand_more'}</span>
        <span className="tp-section-toggle-title">Date</span>
        <span className="tp-section-toggle-value">{formatDate(selectedDate).replace('\n', ' ')}</span>
      </button>
      {!datePickerCollapsed && (
      <div className="date-selector-wrapper">
        <button className="date-scroll-btn date-scroll-left" onClick={() => scrollDates('left')} title="Earlier dates">
          <span className="material-symbols-rounded">chevron_left</span>
        </button>
        <div className="date-selector" ref={dateSelectorRef}>
          {(() => {
            const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const groups = {};
            dates.forEach(date => {
              const key = date.slice(0, 7);
              if (!groups[key]) groups[key] = [];
              groups[key].push(date);
            });
            return Object.entries(groups).map(([monthKey, monthDates]) => {
              const d = new Date(monthKey + '-01T00:00:00');
              const monthLabel = `${monthNames[d.getMonth()].toUpperCase()} ${d.getFullYear()}`;
              return (
                <div key={monthKey} className="month-group">
                  <div className="month-label">{monthLabel}</div>
                  <div className="month-dates">
                    {monthDates.map(date => {
                      const dow = new Date(date + 'T00:00:00').getDay();
                      const isWeekend = dow === 0 || dow === 6;
                      const classes = ['date-btn',
                        selectedDate === date ? 'active' : '',
                        date === todayStr ? 'today' : '',
                        isWeekend ? 'weekend' : ''
                      ].filter(Boolean).join(' ');
                      // Warning dot when this day has any job with unacknowledged
                      // changes. The currently-selected date reads from the live
                      // orders so the dot clears as soon as changes are acknowledged;
                      // other dates come from the fetched set.
                      const hasChanges = date === selectedDate
                        ? orders.some(o => o.has_changes)
                        : datesWithChanges.has(date);
                      return (
                        <button key={date} className={classes} onClick={() => setSelectedDate(date)}>
                          {hasChanges && <span className="date-change-dot" title="Unacknowledged changes on this day" />}
                          {formatDate(date).split('\n').map((line, i) => (
                            <div key={i} className={i === 0 ? 'date-label' : 'date-value'}>{line}</div>
                          ))}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        <button className="date-scroll-btn date-scroll-right" onClick={() => scrollDates('right')} title="Later dates">
          <span className="material-symbols-rounded">chevron_right</span>
        </button>
      </div>
      )}

      {/* Hidden element for measuring text widths */}
      <span ref={measureRef} style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', fontSize: 'var(--font-md)' }} />

      {/* Orders Table */}
      <div className="transport-content" style={{ position: "relative", width: "100%" }}>
        {loading ? (
          <LoadingSpinner />
        ) : orders.length === 0 ? (
          <div className="no-orders">
            <span className="material-symbols-rounded">local_shipping</span>
            <p>No deliveries scheduled for {formatDate(selectedDate).replace('\n', ' ')}</p>
          </div>
        ) : (
          <>
          {/* Isolate-driver banner — stays pinned above the scroll */}
          {isolatedDriver !== null && (
            <div className="isolate-banner">
              <span className="material-symbols-rounded">filter_alt</span>
              <span>
                Showing only <strong>{isolatedDriver === '' ? 'Unassigned' : isolatedDriver}</strong>
                {isolatedDriver !== '' ? ' + unassigned' : ''}
              </span>
              <button className="isolate-clear" onClick={() => setIsolatedDriver(null)}>
                <span className="material-symbols-rounded">close</span> Clear
              </button>
            </div>
          )}
          {showOnlyOptionPlanned && (
            <div className="isolate-banner">
              <span className="material-symbols-rounded">event_repeat</span>
              <span>Showing only orders <strong>planned today with option dates</strong></span>
              <button className="isolate-clear" onClick={() => setShowOnlyOptionPlanned(false)}>
                <span className="material-symbols-rounded">close</span> Clear
              </button>
            </div>
          )}

          <div className="transport-scroll" ref={transportScrollRef}>
          {/* Day summary — collapsible rollup of the day's deliveries */}
          <div className={`day-summary-section ${daySummaryCollapsed ? 'collapsed' : ''}`}>
            <button
              className="tp-section-toggle"
              onClick={() => setDaySummaryCollapsed(c => !c)}
              title={daySummaryCollapsed ? 'Show day summary' : 'Hide day summary'}
            >
              <span className="material-symbols-rounded">{daySummaryCollapsed ? 'chevron_right' : 'expand_more'}</span>
              <span className="tp-section-toggle-title">Day Summary</span>
              <span className="tp-section-toggle-value">{daySummary.orders} orders · {formatTonnes(daySummary.totalTonnes)}</span>
            </button>
            {!daySummaryCollapsed && (
            <div className="day-summary-bar">
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.orders}</span>
                <span className="day-stat-label">Orders</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{formatQuantity(daySummary.totalTonnes)}<span className="day-stat-unit"> T</span></span>
                <span className="day-stat-label">Total Weight</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.addresses}</span>
                <span className="day-stat-label">Delivery Addresses</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.suppliers}</span>
                <span className="day-stat-label">Suppliers/ Mills</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.ourVehicles}</span>
                <span className="day-stat-label">Our Vehicles</span>
              </div>
              <div className="day-stat">
                <span className="day-stat-value">{daySummary.thirdPartyDrivers}</span>
                <span className="day-stat-label">3rd Party Drivers</span>
              </div>
            </div>
            )}
          </div>

          {vehicleLoads.length > 0 && (
            <div className={`vehicle-loads-wrap ${vehicleLoadsCollapsed ? 'collapsed' : ''}`}>
              <button
                className="vehicle-loads-toggle"
                onClick={() => setVehicleLoadsCollapsed(c => !c)}
                title={vehicleLoadsCollapsed ? 'Show vehicle summaries' : 'Hide vehicle summaries'}
              >
                <span className="material-symbols-rounded vl-chevron">{vehicleLoadsCollapsed ? 'chevron_right' : 'expand_more'}</span>
                <span className="vl-toggle-title">Vehicle Summaries</span>
                <span className="vl-toggle-count">{vehicleLoads.length}</span>
              </button>
              {!vehicleLoadsCollapsed && (
              <div className="vehicle-loads">
              {vehicleLoads.map((l) => {
                // Left border + Fits/Won't-fit badge follow FIT; ring + bar follow EFFICIENCY.
                const fitSev = fitSeverity(l);
                const effSev = efficiencySeverity(l);
                const over = l.effPayload != null && l.totalTonnes > l.effPayload;
                // The "Unknown" vehicle carries no compartment spec — only a
                // payload — so it shows payload status rather than a fit verdict.
                const fitLabel = l.isUnknown
                  ? (l.effPayload == null ? 'Set payload' : (over ? 'Over payload' : 'Payload set'))
                  : l.fit === 'ok' ? 'Fits'
                  : l.fit === 'too-many' ? 'Too many drops'
                  : l.fit === 'wont-fit' ? "Won't fit"
                  : l.fit === 'reorder' ? 'Re-sequence'
                  : 'No compartment spec';
                const cardSev = l.isUnknown
                  ? (l.effPayload == null ? 'warn' : (over ? 'danger' : 'ok'))
                  : fitSev;
                const pct = l.effPayload ? Math.min(100, (l.totalTonnes / l.effPayload) * 100) : 0;
                const labelFor = (idx) => {
                  const r = l.rows[idx];
                  return r ? (rowReference(r) || r.customer_name || `Drop ${idx + 1}`) : '?';
                };
                const suggestion = l.suggestedOrder ? l.suggestedOrder.map(labelFor).join(' → ') : null;
                const ringColor = SEVERITY_COLOR[effSev];
                return (
                  <div
                    key={loadKey(l)}
                    className={`vl-card vl-${cardSev} vl-clickable`}
                    onClick={() => setLoadModal({ open: true, key: loadKey(l) })}
                    title={l.isUnknown ? 'Set the payload for this driver’s vehicle' : 'View load details'}
                  >
                    <div className="vl-head">
                      <div className="vl-head-info">
                        <div className="vl-reg-row">
                          <span className="vl-reg">
                            {/* vehicle is idKind 'object' — VehicleModal renders from the
                                record. Unknown-vehicle cards carry a synthetic spec with
                                no id, so they stay plain text. */}
                            <EntityLink type="vehicle" id={l.isUnknown ? null : l.vehicle} variant="badge">
                              {l.registration}
                            </EntityLink>
                          </span>
                          {l.loadCount > 1 && <span className="vl-load-num">{l.loadIndex + 1}/{l.loadCount}</span>}
                          <span className={`vl-chip vl-chip-${cardSev}`}>{fitLabel}</span>
                        </div>
                        <div className="vl-supplier" title={l.supplier}>
                          {l.supplier}{l.timeLabel ? ` · ${l.timeLabel}` : ''}
                        </div>
                        {l.driver && (
                          <div className="vl-driver">
                            <span className="material-symbols-rounded">person</span>
                            <EntityLink type="driver" id={driverIdFor(l.driver)}>{l.driver}</EntityLink>
                          </div>
                        )}
                      </div>
                      {l.efficiency != null && (
                        <ProgressRing percentage={l.efficiency} color={ringColor} size={48} />
                      )}
                    </div>
                    <div className={`vl-bar ${over ? 'vl-bar-over' : `vl-bar-${effSev}`}`}>
                      <div className="vl-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="vl-meta">
                      <strong className={over ? 'vl-over-text' : ''}>{formatQuantity(l.totalTonnes)}</strong>
                      {' / '}
                      {l.effPayload != null ? formatTonnes(l.effPayload) : 'payload n/a'}
                      {l.hasMoffett && l.effPayload != null ? ' (−1 Moffett)' : ''}
                      {' · '}{l.lineCount} drop{l.lineCount === 1 ? '' : 's'}
                    </div>
                    {over && <div className="vl-msg vl-msg-danger">Over payload by {formatTonnes(l.overBy)}</div>}
                    {l.messages.map((m, i) => <div key={i} className="vl-msg vl-msg-danger">{m}</div>)}
                    {suggestion && <div className="vl-msg vl-msg-warn">Suggested drop order: {suggestion}</div>}
                    {l.doorConfig && l.fit !== 'wont-fit' && (
                      <div className="vl-msg vl-msg-muted">Set doors at: {l.doorConfig.map(d => `${d}T`).join(', ')}</div>
                    )}
                  </div>
                );
              })}
              </div>
              )}
            </div>
          )}

          {/* Bulk actions: appears with a selection (tick, drag, or shift-click). */}
          {selectedRows.size > 0 && (
            <div className="bulk-actions-bar">
              <div className="bulk-actions-info">
                <span className="bulk-actions-title">Bulk Actions:</span>
                <span>{selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} selected</span>
              </div>
              <div className="bulk-actions-buttons slideout">
                <div>
                  <button data-write-action className="btn-slideout" onClick={() => setBulkEditOpen(true)}
                    disabled={!!bulkEditBlocked} title={bulkEditBlocked || 'Set haulier, driver, vehicle or time on every selected row'}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">edit_note</span></div>
                    <span className="btn-label">Edit {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''}</span>
                  </button>
                </div>
                <div>
                  <button data-read-safe className="btn-slideout" onClick={() => setSelectedRows(new Set())}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                    <span className="btn-label">Clear</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <table className="transport-table" style={{ position: "relative", width: "100%" }} onContextMenu={ctxMenu.onTableContextMenu} data-time-grid>
            <thead>
              <tr>
                <th className="col-select">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="select-all-transport"
                      checked={selectedRows.size === orders.length && orders.length > 0}
                      onChange={toggleSelectAll}
                    />
                    <label htmlFor="select-all-transport" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                  </label>
                </th>
                <th className="col-num">#</th>
                <th className="col-supplier">SUP/ MILL</th>
                <th className="col-customer">Customer</th>
                <th className="col-instructions">Del. Inst.</th>
                <th className="col-product">Product</th>
                <th className="col-qty">{isSnapshot ? 'P. Qty' : 'Qty'}</th>
                <th className="col-po">REFS</th>
                <th className="col-status">Status</th>
                <th className="col-time">Time</th>
                <th className="col-haulier" style={{ minWidth: colWidths.haulier }}>Haulier</th>
                <th className="col-driver" style={{ minWidth: colWidths.driver }}>Driver</th>
                <th className="col-vehicle" style={{ minWidth: colWidths.vehicle }}>Vehicle</th>
                <th className="col-actions" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {visiblePlanned.map(o => renderPlanningRow(o, false))}
              {visibleCandidates.length > 0 && (
                <tr className="option-section-header" onClick={() => setOptionSectionCollapsed(c => !c)}>
                  <td colSpan={14}>
                    <span className="material-symbols-rounded osh-chevron">{optionSectionCollapsed ? "chevron_right" : "expand_more"}</span>
                    <span className="osh-title">Option-date orders — not planned this day</span>
                    <span className="osh-badge">{visibleCandidates.length}</span>
                  </td>
                </tr>
              )}
              {!optionSectionCollapsed && visibleCandidates.map(o => renderPlanningRow(o, true))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>

      {/* Changes Modal */}
      <Modal
        isOpen={changesModal.open}
        onClose={() => setChangesModal({ open: false, order: null, changes: [], loading: false, hasUnacknowledged: false })}
        title={changesModal.order
          ? `${changesModal.hasUnacknowledged ? 'Recent Changes' : 'Change History'}: ${rowReference(changesModal.order)}`
          : (changesModal.hasUnacknowledged ? 'Recent Changes' : 'Change History')
        }
        size="lg"
        className="transport-changes-modal"
        footer={changesModal.hasUnacknowledged ? (
          <div className="panel-nav-buttons">
            <div>
              <button
                className="btn-slideout"
                onClick={() => {
                  if (changesModal.order) {
                    dismissModified(changesModal.order);
                  }
                  setChangesModal({ open: false, order: null, changes: [], loading: false, hasUnacknowledged: false });
                }}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">done_all</span>
                </div>
                <span className="btn-label">Acknowledge</span>
              </button>
            </div>
          </div>
        ) : null}
      >
        {changesModal.loading ? (
          <div className="loading-state">
            <span className="material-symbols-rounded spinning">sync</span>
            <span>Loading changes...</span>
          </div>
        ) : changesModal.changes.length === 0 ? (
          <div className="empty-state">
            <span className="material-symbols-rounded">history</span>
            <span>No changes recorded{changesModal.hasUnacknowledged ? ' since last acknowledgment' : ''}</span>
          </div>
        ) : (
          <div className="history-timeline">
            {changesModal.changes.map((change, idx) => {
              // Parse context if available
              let context = null;
              if (change.context) {
                try {
                  context = typeof change.context === 'string'
                    ? JSON.parse(change.context)
                    : change.context;
                } catch (e) {
                  // Ignore parse errors
                }
              }

              const hasValues = change.old_value !== null || change.new_value !== null;
              const isExpanded = expandedChanges.has(idx);

              return (
                <div key={idx} className={`timeline-entry ${isExpanded ? 'expanded' : ''}`}>
                  <div className="timeline-marker">
                    <div className="marker-dot"></div>
                    {idx < changesModal.changes.length - 1 && <div className="marker-line"></div>}
                  </div>
                  <div className="timeline-content">
                    <div
                      className="entry-header"
                      onClick={() => { if (hasValues) toggleChangeEntry(idx); }}
                      style={{ cursor: hasValues ? 'pointer' : 'default' }}
                    >
                      <div className="entry-meta">
                        <span className="action-badge action-update">{change.source_label}</span>
                        <span className="entry-field-group">
                          <span className="field-name">{change.field_display || change.field_name}</span>
                          {context?.product && (
                            <span className="entry-product">({context.product})</span>
                          )}
                        </span>
                        <span className="entry-date">{formatChangeTime(change.created_at)}</span>
                      </div>
                      <div className="entry-user">
                        <span className="material-symbols-rounded">person</span>
                        <span>{change.changed_by_name}</span>
                      </div>
                      {hasValues && (
                        <div className="entry-expand">
                          <span className="material-symbols-rounded">expand_more</span>
                        </div>
                      )}
                    </div>
                    {isExpanded && hasValues && (
                      <div className="entry-changes">
                        <div className="changes-list">
                          <div className="change-item">
                            <div className="field-values">
                              <span className="old-value">{formatChangeValue(change.old_value, change.field_name)}</span>
                              <span className="arrow">→</span>
                              <span className="new-value">{formatChangeValue(change.new_value, change.field_name)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Unlock Modal */}
      {unlockModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setUnlockModal({ open: false, password: '', error: null })}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header"  style={{paddingBottom: "var(--spacing-xl)"}}>
              <h3 style={{ margin: "0" }}>Unlock Transport Plan</h3>
              <button className="modal-close" onClick={() => setUnlockModal({ open: false, password: '', error: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: "0 var(--spacing-xl)" }}>
              <p>
                {isPastDate
                  ? 'This day has passed. Enter the transport unlock password to correct who delivered — haulier, driver and vehicle — for the next 30 minutes. Nothing else on a past day changes.'
                  : 'Enter the transport unlock password to unlock editing.'}
              </p>
              <p className="text-muted" style={{ fontSize: '0.85em' }}>
                Forgotten it? Amy, Matt, Jade or an admin can look it up in Transport Settings → Unlock Password.
              </p>
              {unlockModal.error && <p className="error-message">{unlockModal.error}</p>}
              <input
                type="password"
                className="form-input"
                placeholder="Password"
                value={unlockModal.password}
                onChange={e => setUnlockModal(prev => ({ ...prev, password: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleUnlock()}
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={handleUnlock}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">lock_open</span>
                </div>
                <span className="btn-label">Unlock</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Table context menu (right-click) */}
      {bulkEditOpen && (
        <PlanningBulkEditModal
          count={selectedRows.size}
          jobCount={[...selectedRows].filter(isHaulageKey).length}
          hauliers={hauliers}
          drivers={drivers}
          vehicles={vehicles}
          onApply={applyBulkEdit}
          onClose={() => setBulkEditOpen(false)}
        />
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={(row) => {
          const { bulk, count, ids, rows: targetRows } = ctxMenu;
          const optionDayRows = targetRows.filter(isOptionCandidate);
          const selDrivers = bulk ? [...new Set(targetRows.map(o => (o.driver || '').trim()))] : [];
          const isolateTarget = bulk ? (selDrivers[0] ?? '') : (row.driver || '').trim();
          return [
            goToOrderAction(row),
            optionDayRows.length > 0 && {
              key: 'plan-on-day',
              icon: 'event_available',
              label: optionDayRows.length > 1 ? `Plan ${optionDayRows.length} on this day` : 'Plan on this day',
              disabled: planFixed,
              onClick: () => planOnThisDay(optionDayRows)
            },
            {
              key: 'send-top',
              icon: 'vertical_align_top',
              label: bulk ? `Send ${count} rows to top` : 'Send to top',
              disabled: planFixed || count === 0,
              onClick: () => sendIdsToTop(ids)
            },
            {
              key: 'send-to',
              icon: 'low_priority',
              label: bulk ? `Send ${count} rows to…` : 'Send to…',
              disabled: planFixed || count === 0,
              onClick: () => setMoveToModal({ open: true, value: '', targetIds: ids })
            },
            {
              key: 'move-date',
              icon: 'event',
              label: bulk ? `Move ${count} rows to date…` : 'Move to date…',
              // Haulage-job dates are changed from the Haulage Jobs edit screen;
              // the board's move-date only handles customer-order rows.
              // A self-billed PO has no order to move: its date is changed on the PO.
              // Nor a redelivery: its date is changed in its Redelivery window.
              disabled: planFixed || count === 0 || targetRows.some(o => o.is_haulage_job) || targetRows.every(o => o.is_self_billed || o.is_redelivery),
              title: targetRows.some(o => o.is_self_billed) ? SELF_BILLED_DATE_REASON
                : (targetRows.some(o => o.is_redelivery) ? REDELIVERY_DATE_REASON : undefined),
              onClick: () => openMoveDate(targetRows.filter(o => !o.is_self_billed && !o.is_redelivery))
            },
            {
              key: 'change-qty',
              icon: 'scale',
              label: 'Change quantity…',
              // One line at a time; a customer-order line (not a haulage job).
              disabled: planFixed || count !== 1 || !targetRows[0]?.order_line_id || !targetRows[0]?.id || targetRows[0]?.is_haulage_job || !!targetRows[0]?.is_redelivery,
              title: targetRows[0]?.is_redelivery ? REDELIVERY_QTY_REASON : undefined,
              onClick: () => setChangeQtyRow(targetRows[0])
            },
            {
              key: 'move-board',
              icon: 'local_shipping',
              // Haulage jobs are bag-only and cannot move to the bulk board — only
              // bagged/tote customer-order lines can be moved back.
              label: bulk ? `Move ${count} rows to Bulk board` : 'Move to Bulk board',
              disabled: planFixed || count === 0 || targetRows.some(o => o.is_haulage_job || !o.order_line_id || o.is_self_billed || o.is_redelivery),
              title: targetRows.some(o => o.is_self_billed) ? SELF_BILLED_BOARD_REASON
                : (targetRows.some(o => o.is_redelivery) ? REDELIVERY_BOARD_REASON : undefined),
              onClick: async () => {
                const lineIds = targetRows.filter(o => !o.is_haulage_job).map(o => o.order_line_id).filter(Boolean);
                await Promise.all(lineIds.map(id => fetch(`${API_URL}/api/transport/planning/set-board-type`, {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ orderLineId: id, board: 'bulk' })
                }).catch(e => console.error('Move to bulk board failed:', e))));
                fetchOrders();
              }
            },
            // One row; a selection gets this from the bulk actions instead.
            ...(bulk ? [] : [{
              key: 'split-run',
              icon: 'call_split',
              label: 'Split from run',
              disabled: planFixed || !row.run_key,
              title: row.run_key ? undefined : 'Not part of a combined run',
              onClick: () => splitFromRun([row])
            }]),
            {
              key: 'isolate',
              icon: 'filter_alt',
              label: bulk ? 'Isolate driver' : (isolateTarget ? `Isolate ${isolateTarget}` : 'Isolate unassigned'),
              disabled: planFixed || (bulk && selDrivers.length > 1),
              onClick: () => setIsolatedDriver(isolateTarget)
            },
            // Refused — redeliver… / Edit redelivery… (hooks/useBoardRedeliver).
            ...rowRedeliver.actionsFor(row),
            // Archive order… / Archive PO… — the same pair as the three-dots menu.
            ...rowArchive.actionsFor(row)
          ];
        }}
        bulkActions={(ids) => [
          {
            key: 'bulk-edit',
            icon: 'edit_note',
            label: `Set haulier / driver / vehicle / time on ${ids.size} rows…`,
            disabled: !!bulkEditBlocked,
            title: bulkEditBlocked || undefined,
            onClick: () => setBulkEditOpen(true)
          },
          {
            key: 'group',
            icon: 'layers',
            label: `Group ${ids.size} rows`,
            disabled: planFixed,
            onClick: () => groupIds(new Set(ids))
          },
          {
            key: 'combine',
            icon: 'merge',
            label: `Combine ${ids.size} rows into one run`,
            disabled: planFixed,
            onClick: () => setCombineRunModal({ open: true, value: '', targetIds: new Set(ids) })
          },
          {
            key: 'split-run',
            icon: 'call_split',
            label: `Split ${ids.size} rows from their run`,
            disabled: planFixed || !orders.some(o => ids.has(o.order_line_id) && o.run_key),
            onClick: () => splitFromRun(orders.filter(o => ids.has(o.order_line_id)))
          }
        ]}
        actions={[
          {
            key: 'auto-group',
            icon: 'group_work',
            label: 'Auto-group by driver',
            disabled: planFixed,
            onClick: autoGroupByDriver
          },
          {
            key: 'colours',
            icon: 'palette',
            label: 'Toggle colours',
            active: driverColorsOn,
            onClick: () => setDriverColorsOn(v => !v)
          },
          {
            key: 'option-planned',
            icon: 'event_repeat',
            label: 'Show only planned-with-options',
            active: showOnlyOptionPlanned,
            onClick: () => setShowOnlyOptionPlanned(v => !v)
          },
          isolatedDriver !== null && {
            key: 'clear-isolate',
            icon: 'filter_alt_off',
            label: 'Clear isolate',
            onClick: () => setIsolatedDriver(null)
          }
        ]}
      />

      {/* Move to… row-number prompt */}

      {moveToModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setMoveToModal({ open: false, value: '', targetIds: null })}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-xl)' }}>
              <h3 style={{ margin: '0' }}>Move {moveToModal.targetIds ? moveToModal.targetIds.size : 0} Row{(moveToModal.targetIds ? moveToModal.targetIds.size : 0) !== 1 ? 's' : ''}</h3>
              <button className="modal-close" onClick={() => setMoveToModal({ open: false, value: '', targetIds: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl)' }}>
              <p>Enter a row number — the {(moveToModal.targetIds ? moveToModal.targetIds.size : 0) > 1 ? 'rows are grouped and moved' : 'row is moved'} directly beneath it.</p>
              <input
                type="number"
                min="1"
                max={plannedCount}
                className="form-input"
                placeholder={`Row number (1–${plannedCount})`}
                value={moveToModal.value}
                onChange={e => setMoveToModal(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && applyMoveTo()}
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={applyMoveTo}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">low_priority</span>
                </div>
                <span className="btn-label">Move</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Combine rows into one run (shared collection time) */}
      {combineRunModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setCombineRunModal({ open: false, value: '', targetIds: null })}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-xl)' }}>
              <h3 style={{ margin: '0' }}>Combine {combineRunModal.targetIds ? combineRunModal.targetIds.size : 0} Rows into One Run</h3>
              <button className="modal-close" onClick={() => setCombineRunModal({ open: false, value: '', targetIds: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl)' }}>
              <p>These rows become one run for one vehicle: collected in board order, then delivered. Each keeps its own collection time — say 06:00 at one mill and 07:00 at the next — unless you enter one here for them all.</p>
              <input
                type="text"
                className="form-input"
                placeholder="Optional — e.g. 9, 14:30 or 2pm"
                maxLength={7}
                value={combineRunModal.value}
                onChange={e => setCombineRunModal(prev => ({ ...prev, value: e.target.value }))}
                onBlur={e => {
                  const n = normalizeTimeValue(e.target.value);
                  if (n) setCombineRunModal(prev => ({ ...prev, value: n }));
                }}
                // Enter stores the typed time and combines in one go. The value
                // is normalised into the field first so an invalid entry is left
                // tidied up (and visible) when the guard sends it back.
                onKeyDown={e => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  const n = normalizeTimeValue(e.currentTarget.value);
                  if (n) setCombineRunModal(prev => ({ ...prev, value: n }));
                  applyCombineRun();
                }}
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={applyCombineRun}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">merge</span>
                </div>
                <span className="btn-label">Combine</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Move to another delivery date */}
      {moveDateModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setMoveDateModal({ open: false, value: '', orderIds: [], count: 0, optionDates: [] })}>
          <div className="modal-content modal-sm move-delivery-date-modal" style={{ height: 'fit-content', maxHeight: '90vh', minWidth: '400px', maxWidth: '90vw', width: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-md)' }}>
              <h3 style={{ margin: '0' }}>Move to Delivery Date</h3>
              <button className="modal-close" onClick={() => setMoveDateModal({ open: false, value: '', orderIds: [], count: 0, optionDates: [] })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-md) var(--spacing-xl) var(--spacing-xl)' }}>
              <p style={{ fontSize: '0.9em', margin: '0 0 var(--spacing-sm)' }}>
                Move {moveDateModal.count} row{moveDateModal.count === 1 ? '' : 's'}
                {moveDateModal.orderIds.length !== moveDateModal.count ? ` (${moveDateModal.orderIds.length} order${moveDateModal.orderIds.length === 1 ? '' : 's'})` : ''}
                {' '}to a new delivery date.
              </p>
              <p style={{ fontSize: '0.75em', color: 'var(--text-muted)' }}>
                This updates the delivery date on the whole order and its purchase order(s).
              </p>
              <DateInput
                value={moveDateModal.value}
                onChange={e => setMoveDateModal(prev => ({ ...prev, value: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && applyMoveDate()}
              />
              {moveDateModal.optionDates.length > 0 && (
                <div className="move-date-options">
                  <div className="move-date-options-label">Or select from option dates for this order:</div>
                  <div className="move-date-options-list">
                    {moveDateModal.optionDates.map((od) => (
                      <button
                        key={od.date}
                        type="button"
                        className={`move-date-option ${moveDateModal.value === od.date ? 'active' : ''}`}
                        onClick={() => setMoveDateModal(prev => ({ ...prev, value: od.date }))}
                      >
                        {new Date(od.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                        <span className="move-date-option-pref">P{od.preference}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={applyMoveDate} disabled={!moveDateModal.value}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">event</span>
                </div>
                <span className="btn-label">Move</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Customer / Order Notes */}
      {notesModal.open && createPortal(
        <div className="modal-overlay" onClick={() => setNotesModal({ open: false, order: null })}>
          <div className="modal-content modal-sm transport-notes-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ paddingBottom: 'var(--spacing-md)' }}>
              <h3 style={{ margin: '0' }}>Customer / Order Notes</h3>
              <button className="modal-close" onClick={() => setNotesModal({ open: false, order: null })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl) var(--spacing-xl)' }}>
              {notesModal.order && (
                <div className="notes-subtitle">
                  {notesModal.order.customer_name}
                  {rowReference(notesModal.order) ? ` · ${rowReference(notesModal.order)}` : ''}
                </div>
              )}
              {notesModal.order && (notesModal.order.customer_notes?.trim() || notesModal.order.order_notes?.trim() || notesModal.order.customer_largest_vehicle_access?.trim()) ? (
                <>
                  {notesModal.order.customer_notes?.trim() && (
                    <div className="notes-block">
                      <div className="notes-block-label">Customer Account</div>
                      <div className="notes-block-body">{notesModal.order.customer_notes}</div>
                    </div>
                  )}
                  {/* What will physically get in. Internal, like the notes. */}
                  {notesModal.order.customer_largest_vehicle_access?.trim() && (
                    <div className="notes-block">
                      <div className="notes-block-label">Largest Vehicle Access</div>
                      <div className="notes-block-body">{notesModal.order.customer_largest_vehicle_access}</div>
                    </div>
                  )}
                  {notesModal.order.order_notes?.trim() && (
                    <div className="notes-block">
                      <div className="notes-block-label">Order</div>
                      <div className="notes-block-body">{notesModal.order.order_notes}</div>
                    </div>
                  )}
                </>
              ) : (
                <p className="notes-empty">No notes recorded on the customer account or order.</p>
              )}
            </div>
          </div>
        </div>, document.body
      )}

      {/* Paperwork Validation Modal */}
      {paperworkModal.open && !paperworkModal.generating && createPortal(
        <div className="modal-overlay" onClick={() => setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 })}>
          <div className="modal-content modal-lg driver-paperwork-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ marginBottom: 'var(--spacing-lg)' }}>
              <h3 style={{ margin: '0' }}>Driver Paperwork</h3>
              <button className="modal-close" onClick={() => setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 })}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '0 var(--spacing-xl) var(--spacing-xl)' }}>
              {paperworkModal.warnings.length > 0 ? (
                <>
                  <div className="warning-banner">
                    <span className="material-symbols-rounded">warning</span>
                    <span>The following orders have missing information:</span>
                  </div>
                  <div className="warnings-list">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>PO #</th>
                          <th>Customer</th>
                          <th>Product</th>
                          <th style={{ textAlign: 'center' }}>Qty</th>
                          <th>Issues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paperworkModal.warnings.map((w, idx) => (
                          <tr key={idx}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {w.order ? <RowReferenceLink order={w.order} /> : w.po_number}
                            </td>
                            <td>
                              <span className="badge-text-row">
                                {w.customer_account_no && (
                                  <span className="account-badge">
                                    <EntityLink type="contact" id={w.customer_account_id} variant="badge">
                                      {w.customer_account_no}
                                    </EntityLink>
                                  </span>
                                )}
                                <EntityLink type="contact" id={w.customer_account_id}>{w.customer}</EntityLink>
                              </span>
                            </td>
                            <td>
                              <span className="badge-text-row">
                                {w.product_code && (
                                  <span className="product-badge">
                                    <EntityLink type="inventory_item" id={w.inventory_item_id} variant="badge">
                                      {w.product_code}
                                    </EntityLink>
                                  </span>
                                )}
                                <span>{w.product}</span>
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>{w.quantity != null ? (formatQuantity(Number(w.quantity)) || '-') : '-'}</td>
                            <td className="warning-issues">{w.issues.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="warning-note">You can continue anyway, or return to fix these issues.</p>
                </>
              ) : (
                <p>All orders are complete and ready for paperwork generation.</p>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-slideout"
                onClick={() => setPaperworkModal({ open: false, warnings: [], generating: false, progress: 0 })}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">arrow_back</span>
                </div>
                <span className="btn-label">Return to Planning</span>
              </button>
              <button
                className="btn-slideout"
                onClick={openHaulierSelection}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">description</span>
                </div>
                <span className="btn-label">Continue</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Paperwork Progress Modal */}
      {paperworkModal.generating && createPortal(
        <div className="modal-overlay">
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generating Driver Paperwork</h3>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="progress-spinner">
                <span className="material-symbols-rounded spinning-anti">sync</span>
              </div>
              <p>Generating driver paperwork and despatch notes...</p>
              <p className="progress-note">This may take a moment.</p>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Haulier Selection Modal */}
      {/* Driver paperwork — shared with the Bag board (PaperworkSendModal). */}
      <PaperworkSendModal
        user={user}
        isOpen={haulierSelectionModal.open}
        onClose={() => setHaulierSelectionModal({ open: false, hauliers: [] })}
        title="Send Driver Paperwork"
        intro="Each driver gets one email with their own deliveries: a top sheet and a despatch note per delivery."
        warning={[
          unplannedSelfBilledWarning(orders, 'driver'),
          fallbackWarning(haulierSelectionModal.hauliers, { sendToOffice: !!haulierSelectionModal.sendToOffice })
        ]}
        sendNoun="driver"
        settingsHint="Transport Settings"
        recipients={haulierSelectionModal.hauliers.map(h => ({
          key: h.key,
          label: h.isDriver ? <EntityLink type="driver" id={h.driverId}>{h.label}</EntityLink> : h.label,
          sublabel: `${h.orderCount} order${h.orderCount === 1 ? '' : 's'}`,
          emails: h.emails,
          settingsHint: h.settingsHint,
          note: fallbackNote(h),
          canAddEmail: h.canAddEmail,
          include: h.include
        }))}
        onToggle={toggleHaulier}
        onToggleAll={(on) => setHaulierSelectionModal(prev => ({ ...prev, hauliers: prev.hauliers.map(h => ({ ...h, include: on })) }))}
        sendToOffice={!!haulierSelectionModal.sendToOffice}
        onSendToOfficeChange={(on) => setHaulierSelectionModal(prev => ({ ...prev, sendToOffice: on }))}
        copyToOffice={!!haulierSelectionModal.copyToOffice}
        onCopyToOfficeChange={(on) => setHaulierSelectionModal(prev => ({ ...prev, copyToOffice: on }))}
        onSaveEmail={saveRecipientEmail}
        officeEmail={officeEmail}
        generating={!!haulierSelectionModal.generating}
        generatingText="Sending paperwork to driver(s)/ haulier(s)..."
        onSend={generatePaperwork}
      />

      {/* Office Summary Modal — shared with the other board */}
      {officeSummaryModal.open && (
        <OfficeSummaryModal
          warning={unplannedSelfBilledWarning(boardOrders, 'office')}
          generating={officeSummaryModal.generating}
          onClose={() => setOfficeSummaryModal({ open: false, generating: false })}
          onSend={sendOfficeSummary}
          user={user}
        />
      )}

      {/* Supplier Selection Modal */}
      {/* Mill order summaries — shared with the Bag board (PaperworkSendModal). */}
      <PaperworkSendModal
        user={user}
        isOpen={supplierSelectionModal.open}
        onClose={() => setSupplierSelectionModal({ open: false, suppliers: [] })}
        title="Send to Mills / Suppliers"
        intro="Each mill gets one email with its own orders for the day, with the spreadsheet attached."
        sendNoun="mill"
        settingsHint="Transport Settings → Mill Emails"
        recipients={supplierSelectionModal.suppliers.map(x => ({
          key: x.key,
          label: x.name,
          sublabel: `${x.orderCount} order${x.orderCount === 1 ? '' : 's'}`,
          emails: x.emails,
          emailNote: x.emailNote,
          include: x.include
        }))}
        onToggle={toggleSupplier}
        onToggleAll={(on) => setSupplierSelectionModal(prev => ({ ...prev, suppliers: prev.suppliers.map(x => ({ ...x, include: on })) }))}
        sendToOffice={!!supplierSelectionModal.sendToOffice}
        onSendToOfficeChange={(on) => setSupplierSelectionModal(prev => ({ ...prev, sendToOffice: on }))}
        officeEmail={officeEmail}
        generating={!!supplierSelectionModal.generating}
        generatingText="Sending order summaries to mills..."
        onSend={sendSupplierEmails}
      />

      <PaperworkHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, kind: null })}
        date={selectedDate}
        kind={historyModal.kind}
        title={historyModal.kind === 'paperwork' ? 'Driver Paperwork History' : 'Mill Email History'}
      />
      <PlanHistoryModal isOpen={planHistoryOpen} onClose={() => setPlanHistoryOpen(false)} date={selectedDate} initialTab={planHistoryTab} />

      {/* Coming Soon Modal */}
      {comingSoonModal && createPortal(
        <div className="modal-overlay" onClick={() => setComingSoonModal(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send to Mills</h3>
              <button className="modal-close" onClick={() => setComingSoonModal(false)}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
              <span className="material-symbols-rounded" style={{ fontSize: '48px', color: 'var(--color-primary)' }}>construction</span>
              <p style={{ marginTop: '1rem' }}>This feature is coming soon!</p>
              <p className="progress-note">Automated mill orders will be available in a future update.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-slideout" onClick={() => setComingSoonModal(false)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
                <span className="btn-label">OK</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* Revised POs after a date move — see offerResend. */}
      <ChangeQuantityModal row={changeQtyRow} onClose={() => setChangeQtyRow(null)} onDone={onQuantityChanged} />

      {resendOffer && !resendFlow && (
        <ConfirmDialog
          isOpen
          type="info"
          title="Send the revised purchase order?"
          message={resendOffer.message}
          confirmText="Send now"
          cancelText="Not now"
          onConfirm={() => setResendFlow(resendOffer.pos)}
          onCancel={() => setResendOffer(null)}
        />
      )}
      {resendFlow && (
        <SendToSupplierFlow
          linkedPos={resendFlow}
          orderNumber={resendFlow.map(p => p.purchase_order_number).join(', ')}
          user={user}
          onSent={() => setLastAutoSaved(new Date())}
          onClose={() => { setResendFlow(null); setResendOffer(null); fetchOrders(); }}
        />
      )}

      {/* Archive order… / Archive PO… (hooks/useBoardArchive) */}
      {rowArchive.element}
      {/* Refused — redeliver… (hooks/useBoardRedeliver) */}
      {rowRedeliver.element}
      {/* Outside haulier, no PO-H: raise and send it? (hooks/useOutsideHaulierPoh) */}
      {outsidePoh.element}

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.open}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ open: false, title: '', message: '', type: 'info' })}
      />

      <VehicleLoadModal
        isOpen={loadModal.open && !!activeLoad}
        load={activeLoad}
        vehicle={activeLoad?.vehicle}
        // A locked or past day keeps its plan: the window's layouts are shown
        // but greyed with the reason (the server refuses them too).
        onApply={canEdit && !planFixed ? applyLoadConfig : null}
        applyBlocked={!canEdit ? noEditReason
          : planFixed ? (isPastDate ? 'This day has passed — its layout is kept as planned' : 'This day is locked — unlock it to change the layout') : null}
        onSetUnknownPayload={(canEdit && activeLoad?.isUnknown)
          ? (payload) => setUnknownPayload(activeLoad.unknownDriver, payload)
          : null}
        onClose={() => setLoadModal({ open: false, key: null })}
      />

      {/* "Go to order" from a row's context menu. Passing onEdit is what puts the
          edit button in the modal header (it's hidden once the order is invoiced). */}
      {orderModalId && (
        <CustomerOrderModal
          orderId={orderModalId}
          onClose={() => setOrderModalId(null)}
          onEdit={onNavigate ? handleEditOrder : undefined}
        />
      )}
    </div>
  );
}

export default HaulageJobsPlanning;
